import type { WalletCashierSettings } from '../../features/wallet/api';
import { DepositImportantNotice } from '../../features/wallet/DepositImportantNotice';
import { DepositReturnDialog } from '../../features/wallet/DepositReturnDialog';
import { isUnpaidDeposit, proofSupported } from '../../shared/wallet/unpaidDeposits';
import '../../features/wallet/unpaidDeposits.css';
import { orderStatusBucket, orderStatusLabelKey as paymentStatusLabelKey } from '../../shared/wallet/orderStatus';
import { normalizeOrders, orderRouteId, decodeRouteId, recentOrders, type UiOrder } from '../../shared/wallet/orderView';
import { ContactVerifySheet } from './MePage';
import { refreshWallet, refreshWalletFinancials } from '../../shared/wallet/refreshWallet';
import { DepositNotice } from '../../shared/wallet/depositNotice';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowLeft,
  ArrowUpCircle,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  CreditCard,
  ExternalLink,
  Gift,
  Hourglass,
  Info,
  Landmark,
  Lock,
  Minus,
  Plus,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Ticket,
  Trophy,
  Users,
  Wallet,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { SheetPortal } from '../../shared/ui/SheetPortal';
import { useAppStore } from '../../app/appStore';
import { userWalletFiat } from '../../entities/user/types';
import { useAuthStore } from '../../features/auth/authStore';
import { defaultDepositAmount, defaultDepositBenefit, defaultDepositCoupon, preferredDepositAmount } from '../../shared/wallet/depositDefaults';
import {
  createWalletDeposit,
  markWalletDepositCheckoutReturned,
  syncWalletDeposit,
  fetchDepositCouponsForAmount,
  type DepositCoupon,
  createWalletWithdrawal,
  fetchBonusAuditStatus,
  fetchWithdrawalEligibility,
  fetchRaffleData,
  fetchTicketLedger,
  fetchWalletData,
  fetchRecentDeposits,
  type TicketLedgerEntry,
  type TicketLedgerResponse,
  type RaffleData,
  type RaffleEntry,
  type RaffleRecentEvent,
  type RaffleTicket,
  type RaffleWinner,
  type WalletOrder,
  type WalletPaymentChannel,
  type WithdrawalEligibility,
  type WalletStatementItem, fetchWalletStatementBefore } from '../../features/wallet/api';
import { fetchMaintenance } from '../../features/maintenance/api';
import { openSupportChatwoot } from '../../features/support/chatwoot';
import { BrandOwl } from '../../shared/brand/BrandOwl';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { STATEMENT_PAGE_SIZE, mergeStatementPages, nextStatementCursor } from '../../shared/wallet/statementPaging';
import { withTrueRate } from '../../shared/wallet/couponCopy';
import type { MessageKey } from '../../shared/i18n/messages';
import { fiatCurrencySymbol, fmtMoney, fmtAmount, fmtInt } from '../../shared/lib/format';
import { brDayLabel, localDateTime, localDayKey, localDayShort, localListTime } from '../../shared/lib/datetime';
import { drawnSerialFrom, moduloDecimalString } from '../../shared/lib/raffleDrawnSerial';
import { ticketLabel, ticketSerial } from '../../shared/raffle/ticketSerial';
import { fetchMyBets } from '../../features/room/api';
import { betProgram, gameNameKey, pickLabel } from '../../shared/game/betOptions';
import { programSlotSeconds, raffleAnchorRound, roundDateFromRound, roundSeqFromRound } from '../../shared/game/roundLabel';
import { resolveAvatarUrl } from '../../shared/profile/avatarDefaults';
import type { GameCode } from '../../entities/room/types';
import { readStorage, removeStorage, writeStorage } from '../../shared/lib/storage';
import { currentUnixSeconds, formatRaffleCountdown } from '../../shared/lib/raffleCountdown';
import { raffleAdditionalEntryCapacity } from '../../shared/lib/raffleJourneyNavigation';
import { CountryFlag } from '../../shared/country/CountryFlag';
import { useCountryStore } from '../../shared/country/countryStore';
import { marketCopyKey } from '../../shared/country/marketCopy';
import {
  depositQuickAmounts,
  validCashierAmount,
  firstLocalFiatChannel,
  isInternalPaymentChannel,
  localDepositFallback,
} from '../../shared/wallet/paymentChannels';
import { balanceCardView } from '../../shared/wallet/balanceCardView';

type WalletTab = 'statement' | 'orders' | 'tickets';
type WalletFilter = 'all' | 'deposit' | 'withdraw' | 'bet' | 'reward';

const FILTERS: Array<{ key: WalletFilter; labelKey: 'walletFilterAll' | 'walletFilterDeposit' | 'walletFilterWithdraw' | 'walletFilterBet' | 'walletFilterReward' }> = [
  { key: 'all', labelKey: 'walletFilterAll' },
  { key: 'deposit', labelKey: 'walletFilterDeposit' },
  { key: 'withdraw', labelKey: 'walletFilterWithdraw' },
  { key: 'bet', labelKey: 'walletFilterBet' },
  { key: 'reward', labelKey: 'walletFilterReward' },
];

const PAYEE_STORAGE_KEY = 'sorteia.payee';
const PENDING_DEPOSIT_TRACKING_KEY = 'sorteia.pendingDepositTracking';
const PENDING_DEPOSIT_RETURN_READY_KEY = 'sorteia.pendingDepositReturnReady';
const DEPOSIT_RETURN_DIALOG_SEEN_PREFIX = 'sorteia.depositReturnDialogSeen.';

type Payee = {
  nome: string;
  type: 'cpf' | 'email' | 'phone' | 'evp' | 'cvu' | 'cbu';
  taxId: string;
  document?: string;
};

type WithdrawStep = 'payee-cta' | 'payee-form' | 'amount' | 'confirm' | 'status';

export function WalletPage() {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const guestMode = useAuthStore((state) => state.guestMode);
  const logout = useAuthStore((state) => state.logout);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const route = useAppStore((state) => state.route);
  const walletTargetAction = useAppStore((state) => state.walletTargetAction);
  const depositCouponPrefill = useAppStore((state) => state.depositCouponPrefill);
  const setDepositCouponPrefill = useAppStore((state) => state.setDepositCouponPrefill);
  const depositCouponPreselectId = useAppStore((state) => state.depositCouponPreselectId);
  const setDepositCouponPreselectId = useAppStore((state) => state.setDepositCouponPreselectId);
  const setRoute = useAppStore((state) => state.setRoute);
  const setAuditReturn = useAppStore((state) => state.setAuditReturn);
  const setActiveOrderId = useAppStore((state) => state.setActiveOrderId);
  const openWalletTicketsFlow = useAppStore((state) => state.openWalletTicketsFlow);
  const setWalletTargetAction = useAppStore((state) => state.setWalletTargetAction);
  const goBack = useAppStore((state) => state.goBack);
  const showToast = useAppStore((state) => state.showToast);
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const country = useCountryStore((state) => state.country);
  const [activeTab, setActiveTab] = useState<WalletTab>(() => route === 'pedidos' ? 'orders' : route === 'wallet.tickets' ? 'tickets' : 'statement');
  const [filter, setFilter] = useState<WalletFilter>('all');
  const [visibleLimit, setVisibleLimit] = useState(30);
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositPayer, setDepositPayer] = useState({ name: '', cpf: '' });
  const [depositQuoteAmount, setDepositQuoteAmount] = useState(0);
  const [depositOrder, setDepositOrder] = useState<WalletOrder | null>(null);
  const [depositReturnMonitoring, setDepositReturnMonitoring] = useState(false);
  const [returnDialogDismissed, setReturnDialogDismissed] = useState<string>('');
  const [awaitingExternalDepositReturn, setAwaitingExternalDepositReturn] = useState(false);
  const [embeddedCheckoutOpen, setEmbeddedCheckoutOpen] = useState(false);
  const [depositOfferSelected, setDepositOfferSelected] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  const [appliedCouponBonus, setAppliedCouponBonus] = useState(0);
  const [verifyPhoneOpen, setVerifyPhoneOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawStep, setWithdrawStep] = useState<WithdrawStep>('payee-cta');
  const [payee, setPayee] = useState<Payee | null>(() => readPayee());
  const [payeeForm, setPayeeForm] = useState<Payee>(() => readPayee() || { nome: '', type: 'cpf', taxId: '' });
  const [withdrawAmount, setWithdrawAmount] = useState(0);
  const [withdrawRequest, setWithdrawRequest] = useState<WalletOrder | null>(null);
  const [withdrawAbandonBonus, setWithdrawAbandonBonus] = useState(false);
  const [ticketsView, setTicketsView] = useState<'results' | 'tickets'>('results');
  const [raffleNowSeconds, setRaffleNowSeconds] = useState(currentUnixSeconds);
  const elapsedRaffleRefreshRef = useRef('');
  const externalCheckoutWindowRef = useRef<Window | null>(null);
  const externalCheckoutBlurredRef = useRef(false);
  const depositSyncInFlightRef = useRef(false);
  const depositNoticeRef = useRef(new DepositNotice());
  useEffect(() => {
    if (route !== 'wallet' || walletTargetAction === 'withdraw') depositNoticeRef.current.end();
  }, [route, walletTargetAction]);
  useEffect(() => () => { depositNoticeRef.current.end(); }, []);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (route === 'pedidos') setActiveTab('orders');
    else if (route === 'wallet.tickets') setActiveTab('tickets');
    else if (route === 'wallet') setActiveTab('statement');
  }, [route]);

  const walletQuery = useQuery({
    queryKey: ['wallet', token, country.code, country.fiatCurrency, 'summary'],
    queryFn: () => fetchWalletData(token || '', { fullOrderHistory: false }),
    enabled: !!token && !guestMode,
  });
  const bonusAuditQuery = useQuery({
    queryKey: ['wallet-bonus-audit', token],
    queryFn: () => fetchBonusAuditStatus(token || ''),
    enabled: !!token && !guestMode,
    retry: false,
  });
  const withdrawalEligibilityQuery = useQuery({
    queryKey: ['wallet-withdrawal-eligibility', token],
    queryFn: () => fetchWithdrawalEligibility(token || ''),
    enabled: !!token && !guestMode,
    retry: false,
  });
  const withdrawalQuoteQuery = useQuery({
    queryKey: ['wallet-withdrawal-eligibility', token, withdrawAmount],
    queryFn: () => fetchWithdrawalEligibility(token || '', withdrawAmount),
    enabled: !!token && !guestMode && withdrawOpen && withdrawAmount > 0,
    retry: false,
  });

  // Keep the statement, total balance, source locks and withdrawal limits fresh together.
  const refreshBalance = useCallback(() => {
    if (token) void refreshWallet(queryClient, token);
  }, [queryClient, token]);

  const clearDepositTracking = useCallback(() => {
    setDepositReturnMonitoring(false);
    setAwaitingExternalDepositReturn(false);
    externalCheckoutBlurredRef.current = false;
    removeStorage(sessionStorage, PENDING_DEPOSIT_TRACKING_KEY);
    removeStorage(sessionStorage, PENDING_DEPOSIT_RETURN_READY_KEY);
  }, []);

  const armDepositTracking = useCallback((order: WalletOrder) => {
    const publicId = String(order.publicId || '').trim();
    if (!publicId) return;
    depositNoticeRef.current.arm(publicId);
    writeStorage(sessionStorage, PENDING_DEPOSIT_TRACKING_KEY, publicId);
    // Do not poll while the provider checkout is open. In particular, the
    // sandbox provider can expose a simulated paid result before checkout.
    setDepositReturnMonitoring(false);
    removeStorage(sessionStorage, PENDING_DEPOSIT_RETURN_READY_KEY);
  }, []);

  const beginDepositReturnMonitoring = useCallback(async (order: WalletOrder) => {
    const publicId = String(order.publicId || '').trim();
    if (!token || !publicId) return;
    try {
      const response = await markWalletDepositCheckoutReturned(token, publicId);
      if (response.item) setDepositOrder(response.item);
      writeStorage(sessionStorage, PENDING_DEPOSIT_TRACKING_KEY, publicId);
      writeStorage(sessionStorage, PENDING_DEPOSIT_RETURN_READY_KEY, publicId);
      setAwaitingExternalDepositReturn(false);
      setDepositReturnMonitoring(true);
    } catch {
      // The next return/focus event retries this lightweight local marker.
    }
  }, [token]);

  const syncReturnedDeposit = useCallback(async () => {
    const currentOrder = depositOrder;
    const publicId = String(currentOrder?.publicId || '').trim();
    if (!token || !currentOrder || !publicId || depositSyncInFlightRef.current) return;
    if (orderStatusBucket(currentOrder.status, 'deposit') !== 'pending') return;
    depositSyncInFlightRef.current = true;
    let nextOrder: WalletOrder | undefined;
    try {
      nextOrder = (await syncWalletDeposit(token, publicId)).item;
    } catch {
      // A callback can settle the order before the explicit provider query. In
      // that case the sync endpoint no longer has a pending row; read the
      // player's order list once to observe the callback-settled state.
      try {
        const wallet = await fetchWalletData(token);
        nextOrder = wallet.deposits.find((order) => String(order.publicId || '') === publicId);
      } catch {
        // Keep the pending order armed and retry after two seconds on return.
      }
    } finally {
      depositSyncInFlightRef.current = false;
    }
    if (!nextOrder) return;
    // Ignore a response from an order superseded by another checkout.
    if (readStorage(sessionStorage, PENDING_DEPOSIT_TRACKING_KEY) !== publicId) return;
    setDepositOrder(nextOrder);
    const status = orderStatusBucket(nextOrder.status, 'deposit');
    if (status === 'pending') return;
    clearDepositTracking();
    refreshBalance();
    if (status === 'completed' && depositNoticeRef.current.consume(publicId)) {
      showToast(t('pixDepositSuccess'), 'success');
      // Reopen the native success sheet after the user returns from either a
      // new-tab checkout or an embedded checkout; never reopen provider HTML.
      setDepositOpen(true);
    }
  }, [clearDepositTracking, depositOrder, refreshBalance, showToast, t, token]);

  useEffect(() => {
    const publicId = String(depositOrder?.publicId || '');
    if (!token || route !== 'wallet' || !publicId || !depositOrder ||
        orderStatusBucket(depositOrder.status, 'deposit') !== 'pending') return;
    // Android custom tabs do not reliably deliver blur/focus pairs. Observe
    // backend callbacks independently of the provider-return/sandbox gate.
    let disposed = false;
    let reading = false;
    const check = async () => {
      if (disposed || reading || document.visibilityState !== 'visible' ||
          readStorage(sessionStorage, PENDING_DEPOSIT_TRACKING_KEY) !== publicId) return;
      reading = true;
      try {
        const orders = await fetchRecentDeposits(token);
        if (disposed || readStorage(sessionStorage, PENDING_DEPOSIT_TRACKING_KEY) !== publicId) return;
        const order = orders.find((item) => String(item.publicId || '') === publicId);
        if (!order || orderStatusBucket(order.status, 'deposit') === 'pending') return;
        setDepositOrder(order);
        setEmbeddedCheckoutOpen(false);
        clearDepositTracking();
        refreshBalance();
        if (orderStatusBucket(order.status, 'deposit') === 'completed' && depositNoticeRef.current.consume(publicId)) {
          setDepositOpen(true);
          showToast(t('pixDepositSuccess'), 'success');
        }
      } catch {
        // Connectivity can recover after the PWA resumes; retry on the timer.
      } finally { reading = false; }
    };
    void check();
    const timer = window.setInterval(() => { void check(); }, 2_000);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [depositOrder?.publicId, depositOrder?.status, route, token, clearDepositTracking, refreshBalance, showToast, t]);

  useEffect(() => {
    // A redirect checkout runs outside the game tab. Only start provider
    // polling after that tab/browser has actually lost and then regained focus.
    if (!awaitingExternalDepositReturn || depositOpen || route !== 'wallet') return undefined;
    const noteCheckoutOpened = () => { externalCheckoutBlurredRef.current = true; };
    const detectReturn = () => {
      if (!externalCheckoutBlurredRef.current || document.visibilityState !== 'visible' || !document.hasFocus()) return;
      if (depositOrder) void beginDepositReturnMonitoring(depositOrder);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') noteCheckoutOpened();
      else detectReturn();
    };
    if (!document.hasFocus() || document.visibilityState === 'hidden') noteCheckoutOpened();
    window.addEventListener('blur', noteCheckoutOpened);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', detectReturn);
    return () => {
      window.removeEventListener('blur', noteCheckoutOpened);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', detectReturn);
    };
  }, [awaitingExternalDepositReturn, beginDepositReturnMonitoring, depositOpen, depositOrder, route]);

  useEffect(() => {
    // An external checkout leaves the wallet page visible in its original tab.
    // Poll only there; if the player navigates elsewhere, the pending state is
    // retained but no provider query runs in the background. Embedded checkout
    // uses DepositSheet's own interval after its full-screen frame is closed.
    if (!depositReturnMonitoring || depositOpen || route !== 'wallet') return undefined;
    const pollWhenVisible = () => {
      if (document.visibilityState === 'visible') void syncReturnedDeposit();
    };
    pollWhenVisible();
    const timer = window.setInterval(pollWhenVisible, 2_000);
    document.addEventListener('visibilitychange', pollWhenVisible);
    window.addEventListener('focus', pollWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', pollWhenVisible);
      window.removeEventListener('focus', pollWhenVisible);
    };
  }, [depositOpen, depositReturnMonitoring, route, syncReturnedDeposit]);

  const maintenanceQuery = useQuery({
    queryKey: ['maintenance'],
    queryFn: fetchMaintenance,
    refetchInterval: 30_000,
    retry: false,
  });

  const raffleQuery = useQuery({
    queryKey: ['raffle', token],
    queryFn: () => fetchRaffleData(token || ''),
    enabled: !!token && !guestMode && activeTab === 'tickets',
    staleTime: 15_000,
  });

  // The ticket statement. Shares the ['ticket-ledger', token] key with every
  // other reader so a spend invalidates one cache entry, not several.
  const ticketLedgerQuery = useQuery({
    queryKey: ['ticket-ledger', token],
    queryFn: () => fetchTicketLedger(token || ''),
    enabled: !!token && !guestMode && activeTab === 'tickets',
    staleTime: 15_000,
  });

  // Half-minute, not per-second: the only thing here that needed a live clock
  // was the Participate countdown, and that moved to the Draws page. What is
  // left reads the clock at day granularity ("today"/"yesterday") plus one
  // refetch when a draw's time passes, so a 1s interval was re-rendering the
  // whole panel for nothing.
  useEffect(() => {
    if (activeTab !== 'tickets') return undefined;
    const tick = () => setRaffleNowSeconds(currentUnixSeconds());
    tick();
    const timer = window.setInterval(tick, 30_000);
    return () => window.clearInterval(timer);
  }, [activeTab]);

  useEffect(() => {
    const event = raffleQuery.data?.event;
    const scheduledAt = Number(event?.scheduled_at || 0);
    const key = event ? eventId(event) : '';
    if (activeTab !== 'tickets' || event?.status !== 'registering' || !scheduledAt || raffleNowSeconds < scheduledAt) return;
    if (!key || elapsedRaffleRefreshRef.current === key) return;
    elapsedRaffleRefreshRef.current = key;
    void raffleQuery.refetch();
  }, [activeTab, raffleNowSeconds, raffleQuery.data?.event, raffleQuery.refetch]);

  function leaveGuest(nextRoute: 'auth.login' | 'auth.register' | 'auth' = 'auth.register') {
    clearGuest();
    logout();
    setRoute(nextRoute);
  }

  const data = walletQuery.data;
  useEffect(() => {
    const trackedPublicId = readStorage(sessionStorage, PENDING_DEPOSIT_TRACKING_KEY);
    if (!trackedPublicId || !data?.deposits?.length) return;
    const tracked = data.deposits.find((order) => String(order.publicId || '') === trackedPublicId);
    if (!tracked) return;
    setDepositOrder(tracked);
    if (orderStatusBucket(tracked.status, 'deposit') === 'pending') {
      if (readStorage(sessionStorage, PENDING_DEPOSIT_RETURN_READY_KEY) === trackedPublicId) {
        setDepositReturnMonitoring(true);
      } else {
        setAwaitingExternalDepositReturn(true);
      }
      return;
    }
    clearDepositTracking();
    if (orderStatusBucket(tracked.status, 'deposit') === 'completed' && depositNoticeRef.current.consume(trackedPublicId)) setDepositOpen(true);
  }, [clearDepositTracking, data?.deposits]);
  useEffect(() => {
    if (!depositOrder?.publicId) return;
    const refreshed = (data?.deposits || []).find((order) => String(order.publicId || '') === String(depositOrder.publicId));
    if (refreshed) setDepositOrder(refreshed);
  }, [data?.deposits, depositOrder?.publicId]);
  const activeTicketCount = useMemo(() => ticketsForUser(raffleQuery.data?.tickets || [], user?.id == null ? '' : String(user.id)).filter((ticket) => ticket.status === 'active').length, [raffleQuery.data?.tickets, user?.id]);
  const orders = useMemo(() => normalizeOrders(data?.deposits || [], data?.withdrawals || []), [data?.deposits, data?.withdrawals]);
  const returnDialogOrder = depositReturnMonitoring && depositOrder && !depositOpen && route === 'wallet'
    && orderStatusBucket(depositOrder.status, 'deposit') === 'pending'
    && proofSupported(depositOrder)
    && returnDialogDismissed !== String(depositOrder.publicId || '')
    && readStorage(localStorage, `${DEPOSIT_RETURN_DIALOG_SEEN_PREFIX}${String(depositOrder.publicId || '')}`) !== '1'
    ? depositOrder : null;
  function dismissReturnDialog(publicId: string) {
    writeStorage(localStorage, `${DEPOSIT_RETURN_DIALOG_SEEN_PREFIX}${publicId}`, '1');
    setReturnDialogDismissed(publicId);
  }
  // Only configured routes for the account currency can submit payments.
  const realDepositChannel = firstLocalFiatChannel(data?.depositChannels, country.fiatCurrency);
  const depositChannel = realDepositChannel || localDepositFallback(country.code, country.fiatCurrency);
  const withdrawChannel = firstLocalFiatChannel(data?.withdrawChannels, country.fiatCurrency);
  const depositSettings = (data?.depositSettings?.asset === country.fiatCurrency ? data.depositSettings : undefined);
  const withdrawSettings = (data?.withdrawSettings?.asset === country.fiatCurrency ? data.withdrawSettings : undefined);
  const orderAttention = orders.filter((order) => orderStatusBucket(order.status, order.kind) === 'pending').length;
  const depositMin = Number(depositSettings?.minAmount ?? 0.01);
  const depositMax = Number(depositSettings?.maxAmount || Infinity);
  const matchingOffers=(depositSettings?.depositOffers||[]).filter(o=>Number(o.amount)<=depositAmount).sort((a,b)=>Number(b.amount)-Number(a.amount));
  const depositOffer=matchingOffers[0];
  const depositOfferBonus=depositOffer?Math.floor(Math.max(Number(depositOffer.bonusFixed),depositAmount*Number(depositOffer.bonusPercent)/100)*100)/100:0;
  useEffect(()=>{if(selectedCouponId!=null)setDepositOfferSelected(false);},[selectedCouponId]);
  const canCreateDeposit = !!depositSettings && validCashierAmount(depositAmount, depositSettings) &&
    depositAmount >= depositMin &&
    depositAmount <= depositMax;

  function closeExternalCheckoutPlaceholder() {
    const checkoutWindow = externalCheckoutWindowRef.current;
    externalCheckoutWindowRef.current = null;
    if (checkoutWindow && !checkoutWindow.closed) checkoutWindow.close();
  }

  function prepareExternalDepositCheckout() {
    if (depositChannel?.checkoutMode === 'iframe') return;
    // Open a harmless same-origin blank page in the user gesture. Once the
    // pay-in order returns its URL we replace it, avoiding popup blocking and
    // keeping the game tab available for return-time reconciliation.
    const checkoutWindow = window.open('', '_blank');
    if (!checkoutWindow) return;
    try { checkoutWindow.opener = null; } catch { /* browser may make opener read-only */ }
    externalCheckoutWindowRef.current = checkoutWindow;
  }

  function openExternalDepositCheckout(checkoutUrl: string) {
    const checkoutWindow = externalCheckoutWindowRef.current;
    externalCheckoutWindowRef.current = null;
    if (checkoutWindow && !checkoutWindow.closed) {
      checkoutWindow.location.replace(checkoutUrl);
      return;
    }
    window.open(checkoutUrl, '_blank');
  }
  useEffect(() => {
    if (!depositOpen || !canCreateDeposit) {
      setDepositQuoteAmount(0);
      return undefined;
    }
    const timer = window.setTimeout(() => setDepositQuoteAmount(depositAmount), 400);
    return () => window.clearTimeout(timer);
  }, [canCreateDeposit, depositAmount, depositOpen]);
  // The sheet opens on a ready-to-pay amount (PM, 2026-09-15). Applied once
  // per open, and only while the box is still empty — a coupon prefill or the
  // player's own typing is never overwritten.
  const depositDefaultApplied = useRef(false);
  useEffect(() => {
    if (!depositOpen) {
      depositDefaultApplied.current = false;
      return;
    }
    if (depositDefaultApplied.current || depositAmount !== 0 || !depositSettings) return;
    depositDefaultApplied.current = true;
    const preset = defaultDepositAmount({
      minAmount: depositMin,
      maxAmount: depositMax,
      quickAmounts: depositChannel?.quickAmounts || [],
      allowCustomAmount: depositSettings.allowCustomAmount !== false,
      preferred: preferredDepositAmount(country.fiatCurrency),
    });
    if (preset > 0) setDepositAmount(preset);
  }, [country.fiatCurrency, depositAmount, depositChannel?.quickAmounts, depositMax, depositMin, depositOpen, depositSettings]);
  const couponsQuery = useQuery({
    queryKey: ['deposit-coupons', token, depositQuoteAmount],
    queryFn: () => fetchDepositCouponsForAmount(token, depositQuoteAmount),
    enabled: !!token && !guestMode && depositOpen && depositQuoteAmount >= depositMin && depositQuoteAmount <= depositMax,
    retry: false,
  });
  const depositCoupons: DepositCoupon[] = depositQuoteAmount === depositAmount ? couponsQuery.data?.items || [] : [];
  const depositCouponsLoading = canCreateDeposit &&
    (depositQuoteAmount !== depositAmount || couponsQuery.isLoading);
  useEffect(() => {
    if (depositCouponsLoading || depositOfferSelected) return;
    if (selectedCouponId != null && depositCoupons.some((c) => c.id === selectedCouponId)) return;
    setSelectedCouponId(defaultDepositCoupon(depositCoupons)?.id ?? null);
  }, [depositCoupons, depositCouponsLoading, depositOfferSelected, selectedCouponId]);
  // Promotions are on by default (PM, 2026-09-15): once the opening amount is
  // quoted, the cashier offer wins over the coupons unless one pays strictly
  // more. Applied once per opening — after that the choice is the player's.
  const depositBenefitDefaultApplied = useRef(false);
  useEffect(() => {
    if (!depositOpen) { depositBenefitDefaultApplied.current = false; return; }
    if (depositBenefitDefaultApplied.current || depositCouponsLoading || !canCreateDeposit) return;
    depositBenefitDefaultApplied.current = true;
    const pick = defaultDepositBenefit({ offerBonus: depositOfferBonus, coupons: depositCoupons });
    if (pick.kind === 'offer') { setDepositOfferSelected(true); setSelectedCouponId(null); }
  }, [canCreateDeposit, depositCoupons, depositCouponsLoading, depositOfferBonus, depositOpen]);
  // Honour the coupon the player tapped in the Cupons tab: select it as soon
  // as the quote for this amount confirms it applies. Consumed once — after
  // that the sheet's own selection is the player's to change.
  useEffect(() => {
    if (depositCouponPreselectId == null) return;
    if (!depositCoupons.some((c) => c.id === depositCouponPreselectId)) return;
    setSelectedCouponId(depositCouponPreselectId);
    setDepositCouponPreselectId(null);
  }, [depositCoupons, depositCouponPreselectId, setDepositCouponPreselectId]);
  const withdrawMin = Number(withdrawSettings?.minAmount ?? 0.01);
  const audit = bonusAuditQuery.data;
  const withdrawablePrincipal = Math.max(0, Number(withdrawalEligibilityQuery.data?.maxWithdrawableAmount || 0));
  const withdrawalQuote = withdrawalQuoteQuery.data;
  useEffect(() => { setWithdrawAbandonBonus(false); }, [withdrawalQuote?.quoteToken]);
  const bonusBalance = Math.max(0, Number(audit?.bonusBalance || 0));
  // How much of the headline total the player can actually take out. Stated as
  // a SUBSET of the total, never as a sum of parts: the card used to print
  // three cells that had to add up to the balance, which forced a third,
  // invented category ("play only" = total - withdrawable - bonus) that exists
  // in no backend field and describes no decision the player can make. A subset
  // leaves nothing unaccounted for without asking anyone to do arithmetic.
  // Older audit responses are BRL-only; never let them replace an ARS balance.
  const auditBalanceMatches = (audit?.asset ?? 'BRL') === country.fiatCurrency;
  const walletTotal = (auditBalanceMatches ? audit?.totalBalance : undefined) ?? userWalletFiat(user);
  const card = balanceCardView({
    total: Number(walletTotal),
    withdrawable: withdrawablePrincipal,
    bonusBalance,
    nextUnlock: audit ? audit.nextUnlock : undefined,
  });
  // The lock sheet and the card quote the same sentence and the same ring so
  // they can never disagree (2026-08-10 review §6, 2026-09-15 spec).
  // Null whenever the backend gave us no milestone to measure against (query in
  // flight or failed, older backend, no active audit, or a zero requirement).
  // The Available / Locked split still renders; only the progress claim is held
  // back, because a ring with nothing behind it invents a number.
  const unlockProgress = (card.state === 'locked' || card.state === 'partial') && card.ring && card.wagerRemaining != null
    ? { ring: card.ring, remaining: card.wagerRemaining }
    : null;
  const nextUnlockLine = unlockProgress
    ? t('walletNextUnlockBet').replace('%v%', fmtMoney(unlockProgress.remaining))
    : null;
  const nextUnlockArcPct = unlockProgress ? unlockProgress.ring.arcPct : 0;
  const [showAuditLock, setShowAuditLock] = useState(false);
  const withdrawMax = Math.min(Number(withdrawSettings?.maxAmount || Infinity), withdrawablePrincipal, withdrawSettings?.dailyRemaining == null ? Infinity : Number(withdrawSettings.dailyRemaining));
  const withdrawFee = feeFor(withdrawSettings, withdrawAmount);
  const withdrawNet = Math.max(0, withdrawAmount - withdrawFee);
  const walletMaintenanceBlocked = maintenanceBlocksWallet(maintenanceQuery.data?.level);
  const canContinueWithdraw = !!withdrawSettings && !!payee && canSavePayee(payee, withdrawChannel?.requiresPayoutDocument) &&
    validCashierAmount(withdrawAmount,withdrawSettings) &&
    withdrawAmount >= withdrawMin &&
    withdrawAmount <= withdrawMax &&
    withdrawNet > 0 &&
    withdrawalQuote?.requestedAmountEligible === true;
  const canSubmitWithdraw = canContinueWithdraw &&
    !withdrawalQuoteQuery.isFetching && !withdrawalQuoteQuery.isError &&
    (!withdrawalQuote?.requiresBonusAbandonment || withdrawAbandonBonus);

  const depositMutation = useMutation({
    mutationFn: () => {
      if (!token) throw new Error('UNAUTHORIZED');
      if (!depositChannel) throw new Error('PAYMENT_CHANNEL_UNAVAILABLE');
      return createWalletDeposit(token, { channelId: depositChannel.channelId, amount: depositAmount, ...(depositChannel.requiresPayerIdentity ? depositPayer : {}), selectedCouponId, selectedOfferId: depositOfferSelected && depositOfferBonus>0 ? depositOffer?.id : null });
    },
    onSuccess: (payload) => {
      const checkoutUrl = safeCheckoutUrl(payload.item?.checkoutUrl);
      const sandboxCheckoutFallback = payload.item?.checkoutSandboxFallback === true;
      if (payload.item) {
        setDepositOrder(payload.item);
        armDepositTracking(payload.item);
      }
      if (checkoutUrl && payload.item?.checkoutMode !== 'iframe') {
        setAwaitingExternalDepositReturn(true);
        setDepositOpen(false);
        openExternalDepositCheckout(checkoutUrl);
        return;
      }
      if ((checkoutUrl || sandboxCheckoutFallback) && payload.item?.checkoutMode === 'iframe') setEmbeddedCheckoutOpen(true);
      closeExternalCheckoutPlaceholder();
      refreshBalance();
    },
    onError: () => closeExternalCheckoutPlaceholder(),
  });

  const withdrawMutation = useMutation({
    mutationFn: () => {
      if (!token) throw new Error('UNAUTHORIZED');
      if (!withdrawChannel) throw new Error('PAYMENT_CHANNEL_UNAVAILABLE');
      if (!payee) throw new Error('PAYEE_REQUIRED');
      return createWalletWithdrawal(token, {
        channelId: withdrawChannel.channelId,
        amount: withdrawAmount,
        destinationType: payee.type,
        destinationRef: formatPayeeRef(payee),
        destinationName: payee.nome,
        destinationDocument: payee.type==='cpf' ? formatPayeeRef(payee) : payee.document,
        abandonBonus: withdrawAbandonBonus,
        quoteToken: withdrawalQuote?.quoteToken,
        confirmedBonusAmount: withdrawalQuote?.forfeitedBonusAmount,
      });
    },
    onSuccess: (payload) => {
      if (payload.item) setWithdrawRequest(payload.item);
      setWithdrawStep('status');
      refreshBalance();
    },
    onError: (error: Error) => {
      if (error.message === 'PHONE_VERIFICATION_REQUIRED') {setWithdrawOpen(false);setVerifyPhoneOpen(true);return;}
      if (['WITHDRAWAL_QUOTE_CHANGED', 'INSUFFICIENT_WITHDRAWABLE_BALANCE', 'BONUS_ABANDONMENT_CONFIRMATION_REQUIRED'].includes(error.message)) {
        setWithdrawAbandonBonus(false);
        withdrawalQuoteQuery.refetch();
        withdrawalEligibilityQuery.refetch();
        setWithdrawStep('confirm');
      } else setWithdrawStep('status');
    },
  });

  // The wallet payload carries the newest 100 rows. Older rows come in pages
  // keyed off the oldest id loaded so far; they are re-keyed (and dropped) when
  // the first page changes, i.e. after a refetch that brought new rows in.
  const firstPage = data?.statement || [];
  const firstPageHead = String(firstPage[0]?.id ?? '');
  const olderPages = useInfiniteQuery({
    queryKey: ['wallet-statement-older', token, country.fiatCurrency, firstPageHead],
    initialPageParam: nextStatementCursor([firstPage]),
    queryFn: ({ pageParam }) => fetchWalletStatementBefore(token!, country.fiatCurrency, String(pageParam), STATEMENT_PAGE_SIZE),
    getNextPageParam: (_last, pages) => nextStatementCursor([firstPage, ...pages.map((p) => p.items || [])]),
    // Only ever fetched on demand (the "load more" button), never on mount.
    enabled: false,
  });
  const allStatement = useMemo(
    () => mergeStatementPages([firstPage, ...(olderPages.data?.pages || []).map((p) => p.items || [])]),
    [firstPage, olderPages.data],
  );
  // Our own cursor decides — react-query's hasNextPage is false until the
  // first older page has been fetched, which would hide the button exactly
  // when it is needed.
  const canLoadOlder = nextStatementCursor([firstPage, ...(olderPages.data?.pages || []).map((p) => p.items || [])]) !== null;
  const statementRows = useMemo(
    () => normalizeStatement(allStatement).filter((row) => rowMatchesFilter(row, filter)),
    [allStatement, filter],
  );
  // A statement can refresh independently (focus, deposit polling, returning to
  // Wallet). A new head row must also refresh every financial summary, even if
  // /me already has the right total. Do not invalidate the statement here.
  const balanceReconciledRef = useRef('');
  useEffect(() => {
    const newest = data?.statement?.[0];
    if (!token || !newest) return;
    const key = JSON.stringify([token, country.code, newest.id, newest.balanceAfter ?? newest.balance_after]);
    if (balanceReconciledRef.current === key) return;
    balanceReconciledRef.current = key;
    void refreshWalletFinancials(queryClient, token);
  }, [data?.statement, country.code, queryClient, token]);
  // Legacy txBetMeta: bet/win rows also say WHICH game and pick (joined by betId)
  const betsMetaQuery = useQuery({
    queryKey: ['wallet-bet-meta', token],
    queryFn: () => fetchMyBets(token!, { limit: 100 }),
    enabled: !!token && activeTab === 'statement',
    staleTime: 60_000,
  });
  const betMeta = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of betsMetaQuery.data?.items || []) {
      const id = item.id != null ? String(item.id) : '';
      const game = String(item.game || '');
      if (!id || !game) continue;
      const pick = pickLabel(game, item.tab, item.pick, t);
      // Milhar 两档带上期号。对其他游戏，选号本身就说清了是哪一注（`Sic Bo · Sum 15`）；
      // 而 Milhar 一次可以押好几期同一个号，`Milhar · 41655` 这一行就分不出是哪一期
      // 的那笔扣款 —— 而这张表恰恰是用来对账的。期号锚定注册国、按档位分槽，与游戏页
      // 和注单历史同一套（doc/TIME_DISPLAY_CONTRACT.md 规则 1）。
      const seq = game === 'milhar'
        ? roundSeqFromRound(
          // 用这一期自己的锚点 `cutoff_ts`，不是下注时刻 —— 整点档收一整个小时的注，
          // 按 placed_at 算会把 13:20 下的那注编到上一期去，和票面对不上
          // （与 BetHistorySheet 同一条口径）。
          { starts_at: Number(item.cutoff_ts || item.placed_at || 0) },
          programSlotSeconds(betProgram(game, item.tab)),
        )
        : '';
      map.set(id, [t(gameNameKey(game as GameCode, item.tab)), seq && `#${seq}`, pick]
        .filter(Boolean).join(' · '));
    }
    return map;
  }, [betsMetaQuery.data, t]);
  const visibleRows = statementRows.slice(0, visibleLimit);
  const recent = useMemo(() => recentOrders(orders, 50), [orders]);
  const headerTitleKey: MessageKey = activeTab === 'tickets' ? 'walletTabBilhetes' : 'wallet';
  const headerEyebrowKey: MessageKey = activeTab === 'tickets' ? 'walletTabBilhetes' : 'walletEyebrow';

  useEffect(() => {
    if (route !== 'wallet' || !walletTargetAction || walletQuery.isLoading) return;
    if (walletMaintenanceBlocked) {
      showToast(t('maintenanceWallet'), 'warn');
      setWalletTargetAction(null, { replace: true });
      return;
    }
    if (walletTargetAction === 'deposit') {
      setWithdrawOpen(false);
      setWithdrawRequest(null);
      setDepositOrder(null);
      setDepositOpen(true);
      if (depositCouponPrefill && depositCouponPrefill > 0) {
        setDepositAmount(depositCouponPrefill);
        setDepositCouponPrefill(null);
      }
    } else if (walletTargetAction === 'withdraw') {
      setDepositOpen(false);
      setDepositOrder(null);
      openWithdrawSheet();
    }
  }, [payee, route, setWalletTargetAction, showToast, t, walletMaintenanceBlocked, walletQuery.isLoading, walletTargetAction, depositCouponPrefill, setDepositCouponPrefill]);

  // The other direction of the effect above: the system back button pops the
  // '#/wallet/deposit' entry, walletTargetAction goes null — and nothing used to
  // close the sheet, so back looked broken until pressed a second time.
  useEffect(() => {
    if (route !== 'wallet' || walletTargetAction) return;
    depositNoticeRef.current.end();
    setDepositOpen(false);
    setWithdrawOpen(false);
  }, [route, walletTargetAction]);

  useEffect(() => {
    if (!walletMaintenanceBlocked || (!depositOpen && !withdrawOpen)) return;
    setDepositOpen(false);
    setWithdrawOpen(false);
    setDepositOrder(null);
    setWithdrawRequest(null);
    showToast(t('maintenanceWallet'), 'warn');
  }, [depositOpen, showToast, t, walletMaintenanceBlocked, withdrawOpen]);

  function openWithdrawSheet(verifiedUser = useAuthStore.getState().user) {
    if (!verifiedUser?.phone || !Boolean(verifiedUser.phone_verified ?? verifiedUser.phoneVerified)) {
      setVerifyPhoneOpen(true);
      return;
    }
    depositNoticeRef.current.end();
    if (walletMaintenanceBlocked) {
      showToast(t('maintenanceWallet'), 'warn');
      if (route === 'wallet' && walletTargetAction === 'withdraw') setWalletTargetAction(null, { replace: true });
      return;
    }
    setWithdrawRequest(null);
    setWithdrawAbandonBonus(false);
    withdrawMutation.reset();
    setWithdrawAmount(0);
    if (payee) {
      setPayeeForm(payee);
      setWithdrawStep(canSavePayee(payee, withdrawChannel?.requiresPayoutDocument) ? 'amount' : 'payee-form');
    } else {
      setPayeeForm({ nome: '', type: 'cpf', taxId: '' });
      setWithdrawStep('payee-cta');
    }
    setWithdrawOpen(true);
  }

  function openDepositSheet() {
    if (walletMaintenanceBlocked) {
      showToast(t('maintenanceWallet'), 'warn');
      if (route === 'wallet' && walletTargetAction === 'deposit') setWalletTargetAction(null, { replace: true });
      return;
    }
    setDepositAmount(0);
    setDepositQuoteAmount(0);
    setSelectedCouponId(null);
    setAppliedCouponBonus(0);
    setEmbeddedCheckoutOpen(false);
    closeExternalCheckoutPlaceholder();
    clearDepositTracking();
    depositNoticeRef.current.end();
    depositMutation.reset();
    setDepositOrder(null);
    setDepositOpen(true);
    setWalletTargetAction('deposit');
  }

  function openWithdrawSheetWithRoute() {
    openWithdrawSheet();
    setWalletTargetAction('withdraw');
  }

  function savePayee() {
    const normalized = normalizePayee(payeeForm);
    if (!canSavePayee(normalized, withdrawChannel?.requiresPayoutDocument)) return;
    setPayee(normalized);
    writeStorage(localStorage, PAYEE_STORAGE_KEY, JSON.stringify(normalized));
    setWithdrawStep('amount');
  }

  function closeWithdrawSheet() {
    setWithdrawOpen(false);
    setWithdrawRequest(null);
    setWithdrawAmount(0);
    withdrawMutation.reset();
    // goBack, not a replace of the URL in place: opening pushed '#/wallet/withdraw',
    // so replacing it left two adjacent entries for the same screen and the first
    // system-back press appeared to do nothing. Popping also means a sheet opened
    // from somewhere else — the room's "not enough balance" CTA pushes straight to
    // '#/wallet/deposit' — closes back to where the user actually was.
    if (route === 'wallet' && walletTargetAction === 'withdraw') goBack('wallet');
    refreshBalance();
  }

  function closeDepositSheet() {
    setDepositOpen(false);
    setEmbeddedCheckoutOpen(false);
    if (depositOrder && orderStatusBucket(depositOrder.status, 'deposit') === 'pending' && depositOrder.checkoutMode !== 'iframe') {
      // Keep an external checkout's local order reference and begin
      // return-time provider polling; no payment URL is retained or shown.
      armDepositTracking(depositOrder);
      setAwaitingExternalDepositReturn(true);
    } else {
      clearDepositTracking();
      setDepositOrder(null);
    }
    depositNoticeRef.current.end();
    closeExternalCheckoutPlaceholder();
    setDepositQuoteAmount(0);
    setSelectedCouponId(null);
    setAppliedCouponBonus(0);
    depositMutation.reset();
    if (route === 'wallet' && walletTargetAction === 'deposit') goBack('wallet');
    refreshBalance();
  }

  function exitEmbeddedDepositCheckout() {
    // Return to the local pending-order view the player left, rather than
    // dismissing the entire deposit flow to Wallet. That view alone now owns
    // the two-second reconciliation interval for this embedded payment.
    setEmbeddedCheckoutOpen(false);
    if (depositOrder && orderStatusBucket(depositOrder.status, 'deposit') === 'pending') {
      if (proofSupported(depositOrder)) setDepositOpen(false); // let the dialog own the wait
      void beginDepositReturnMonitoring(depositOrder);
    }
  }

  function openWithdrawalList() {
    setWithdrawOpen(false);
    setWithdrawRequest(null);
    setWithdrawAmount(0);
    withdrawMutation.reset();
    setActiveOrderId(null);
    setActiveTab('orders');
    if (walletTargetAction === 'withdraw') setWalletTargetAction(null, { replace: true });
    refreshBalance();
    setRoute('pedidos');
  }

  function openHistoryDetail(order: UiOrder) {
    // Route first, then set the order id: setRoute('me.history') pushes
    // '#/me/history' (still route 'pedidos', so it leaves '#/pedidos' as the
    // preceding history entry); setActiveOrderId then replaces that pushed
    // entry with '#/me/history/<id>'. Doing it the other way round replaces
    // '#/pedidos' itself with '#/pedidos/<id>' — which the router remaps back
    // to this same detail page — so a system-back press from the detail
    // landed on the detail again instead of the Recent list.
    setRoute('me.history');
    setActiveOrderId(orderRouteId(order));
  }

  function goToUnpaid(publicId: string) {
    // Same ordering fix as openHistoryDetail above.
    setRoute('pedidos.unpaid');
    setActiveOrderId(publicId);
  }

  if (guestMode) {
    return (
      <section className="tab-page wallet-page">
        <WalletHeader titleKey="wallet" eyebrowKey="walletEyebrow" refreshing={false} onRefresh={() => undefined} />
        <div className="guest-gate-panel" data-wallet-guest-gate-react="true">
          <Wallet size={28} aria-hidden="true" />
          <strong>{t('guestWalletGateTitle')}</strong>
          <p>{t(marketCopyKey('guestWalletGateBody', country.code))}</p>
          <button className="primary-button" type="button" onClick={() => leaveGuest('auth.register')}>
            {t('guestGateCTA')}
          </button>
          <button className="secondary-button" type="button" onClick={() => leaveGuest('auth.login')}>
            {t('guestGateLogin')}
          </button>
          <button className="link-button guest-exit-link" type="button" onClick={() => leaveGuest('auth')}>
            {t('guestExitLink')}
          </button>
        </div>
      </section>
    );
  }

  if (verifyPhoneOpen && token) return <ContactVerifySheet token={token} channel="phone" user={user} onClose={()=>{setVerifyPhoneOpen(false);setWalletTargetAction(null,{replace:true});}} onDone={updated=>{if(updated){useAuthStore.getState().setUser(updated);setVerifyPhoneOpen(false);void queryClient.invalidateQueries({queryKey:['me']});openWithdrawSheet(updated);}}} />;

  return (
    <section className="tab-page wallet-page">
      <WalletHeader
        titleKey={headerTitleKey}
        eyebrowKey={headerEyebrowKey}
        refreshing={walletQuery.isFetching || (activeTab === 'tickets' && (raffleQuery.isFetching || ticketLedgerQuery.isFetching))}
        onRefresh={() => {
          refreshBalance();
          if (activeTab === 'tickets') { raffleQuery.refetch(); ticketLedgerQuery.refetch(); }
        }}
      />

      {/* The money card heads the Statement and Transactions tabs — both are
          about this wallet's money. Tickets is a separate ledger and starts
          directly with its own controls. */}
      {activeTab !== 'tickets' ? (
      <section className="wallet-hero-card" data-card-state={card.state}>
        <div className="wallet-hero-top">
          {/* Flag beside the ISO code for the same reason the header pill carries
              one: `Intl` prints ARS as a bare `$`, so the figure below cannot say
              which market this wallet belongs to on its own. */}
          <span className="wallet-hero-cur">
            <CountryFlag code={country.code} size={15} />
            {t('walletFiat').replace('%currency%', country.fiatCurrency)}
          </span>
          {depositChannel && !isInternalPaymentChannel(depositChannel)
            ? <b>{depositChannel.network || depositChannel.label}</b>
            : null}
        </div>
        <strong>{fmtMoney(walletTotal)}</strong>
        <p>{t('walletFiatSub')}</p>

        {/* Nothing here may be claimed until the bonus audit has actually
            answered. While the query is in flight or after it fails there is no
            `nextUnlock` field to read, and printing "All available to withdraw"
            on that silence promised money to players who had none. */}
        {audit ? (
          card.state === 'all-unlocked' ? (
            <p className="wallet-all-available">
              <CheckCircle2 size={18} aria-hidden="true" />
              <span>{t('walletAllAvailable')}</span>
            </p>
          ) : card.state === 'locked' || card.state === 'partial' ? (
            <div className="wallet-unlock-panel">
              {unlockProgress ? (
                <button
                  type="button"
                  className="wallet-unlock-progress"
                  onClick={() => { setAuditReturn('wallet'); setRoute('me.audit'); }}
                >
                  <UnlockRing pct={unlockProgress.ring.pct} arcPct={unlockProgress.ring.arcPct} />
                  <span className="wallet-unlock-text">
                    <small>{t('walletRolloverEyebrow')}</small>
                    <b className="wallet-next-bet">{nextUnlockLine}</b>
                    {card.unlockAmount != null ? (
                      <span className="wallet-next-gain">
                        {t('walletNextUnlockGain').split('%a%')[0]}
                        <em>{fmtMoney(card.unlockAmount)}</em>
                        {t('walletNextUnlockGain').split('%a%')[1]}
                      </span>
                    ) : null}
                  </span>
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              ) : null}
              <div className="wallet-figures">
                <div className="wallet-figure wallet-figure-available">
                  <span>{t('walletCashWithdrawable')}</span>
                  <b className={card.withdrawable > 0 ? 'is-positive' : ''}>{fmtMoney(card.withdrawable)}</b>
                </div>
                <div className="wallet-figure wallet-figure-locked">
                  <span><Lock size={11} aria-hidden="true" />{t('walletCashLocked')}</span>
                  <b>{fmtMoney(card.locked)}</b>
                  {card.bonus != null ? (
                    <small className="wallet-figure-bonus">{t('walletLockedInclBonus').replace('%b%', fmtMoney(card.bonus))}</small>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null
        ) : null}

        <div className="wallet-action-grid">
          <button className="wallet-hero-action is-deposit" type="button" disabled={walletQuery.isLoading || walletMaintenanceBlocked} onClick={openDepositSheet}>
            <ArrowDownCircle size={17} aria-hidden="true" />
            {t('walletDeposit')}
          </button>
          <button
            className="wallet-hero-action is-withdraw"
            type="button"
            data-locked={withdrawablePrincipal <= 0 ? 'true' : 'false'}
            disabled={walletQuery.isLoading || walletMaintenanceBlocked || withdrawalEligibilityQuery.isLoading}
            onClick={() => {
              if (!withdrawalEligibilityQuery.data?.canWithdraw) setShowAuditLock(true);
              else openWithdrawSheetWithRoute();
            }}
          >
            {withdrawablePrincipal <= 0 ? <Lock size={15} aria-hidden="true" /> : <ArrowUpCircle size={17} aria-hidden="true" />}
            {t('walletWithdraw')}
          </button>
        </div>

        {depositChannel?.network === 'PIX' ? (
          <p className="wallet-trust-line">
            <span><ShieldCheck size={12} aria-hidden="true" />{t('walletTrustRegulated')}</span>
            <span><Landmark size={12} aria-hidden="true" />{t('walletTrustBanks')}</span>
            <span><Zap size={12} aria-hidden="true" />{t(marketCopyKey('walletTrustSpeed', country.code))}</span>
          </p>
        ) : null}
      </section>
      ) : null}

      {showAuditLock ? (
        <SheetPortal>
        <div className="sheet-backdrop" role="presentation" onMouseDown={() => setShowAuditLock(false)}>
          <section className="profile-sheet wallet-lock-sheet" role="dialog" aria-modal="true" aria-label={t('walletLockTitle')} onMouseDown={(event) => event.stopPropagation()}>
            <div className="sheet-grabber" />
            <header className="sheet-header">
              <div>
                <h2>{t('walletLockTitle')}</h2>
              </div>
              <button className="icon-button" type="button" onClick={() => setShowAuditLock(false)} aria-label={t('close')}>×</button>
            </header>
            <p className="wallet-lock-body">{t('walletLockBody')}</p>
            {nextUnlockLine ? (
              <div className="wallet-lock-remaining">
                <span className="wap-track" aria-hidden="true"><i style={{ width: `${nextUnlockArcPct}%` }} /></span>
                <span>{nextUnlockLine}</span>
              </div>
            ) : null}
            <div className="wallet-lock-actions">
              <button type="button" className="is-primary" onClick={() => { setShowAuditLock(false); setAuditReturn('wallet'); setRoute('me.audit'); }}>{t('walletAuditSeeDetails')}</button>
              <button type="button" onClick={() => setShowAuditLock(false)}>{t('close')}</button>
            </div>
          </section>
        </div>
        </SheetPortal>
      ) : null}

      <section className="wallet-panel">
        <nav className="wallet-tabs" aria-label={t('wallet')}>
          <button type="button" className={activeTab === 'statement' ? 'active tab-extrato' : 'tab-extrato'} onClick={() => { setActiveTab('statement'); if (route !== 'wallet') setRoute('wallet'); }}>
            {t('walletTabExtrato')}
          </button>
          <button type="button" className={activeTab === 'orders' ? 'active tab-transactions' : 'tab-transactions'} onClick={() => { setActiveTab('orders'); if (route !== 'pedidos') setRoute('pedidos'); }}>
            {t('walletTabRecent')}
            {orderAttention > 0 ? <em className="wallet-tab-order-chip">{fmtInt(orderAttention)}</em> : null}
          </button>
          <button type="button" className={activeTab === 'tickets' ? 'active tab-bilhetes' : 'tab-bilhetes'} onClick={() => { setActiveTab('tickets'); if (route !== 'wallet.tickets') setRoute('wallet.tickets'); }}>
            {t('walletTabBilhetes')}
            {activeTicketCount > 0 ? <em className="wallet-tab-ticket-chip">{activeTicketCount}</em> : null}
          </button>
        </nav>

        {activeTab === 'statement' ? (
          <div className="wallet-panel-body">
            <div className="wallet-filter-row">
              {FILTERS.map((item) => (
                <button key={item.key} type="button" className={filter === item.key ? 'active' : ''} onClick={() => { setFilter(item.key); setVisibleLimit(30); }}>
                  {t(item.labelKey)}
                </button>
              ))}
            </div>
            {filter === 'deposit' || filter === 'withdraw' ? (
              <div className="wallet-filter-info">
                <Info size={14} aria-hidden="true" />
                <span>{t(filter === 'deposit' ? 'walletFilterInfoDep' : 'walletFilterInfoWdr')}</span>
              </div>
            ) : null}
            {walletQuery.isLoading ? <WalletState text={t('loading')} /> : null}
            {walletQuery.isError ? <WalletState text={t('walletRefreshFailed')} actionLabel={t('retry')} onAction={() => walletQuery.refetch()} /> : null}
            {!walletQuery.isLoading && !walletQuery.isError && visibleRows.length === 0 ? (
              <WalletEmpty filter={filter} onDeposit={filter === 'all' ? openDepositSheet : undefined} />
            ) : null}
            {visibleRows.map((row) => <StatementRow key={row.id} row={row} meta={row.betId ? betMeta.get(row.betId) : undefined} />)}
            {statementRows.length > visibleLimit || canLoadOlder ? (
              <button
                className="wallet-load-more"
                type="button"
                disabled={olderPages.isFetching}
                onClick={() => {
                  // Reveal what is loaded first; when that runs out, fetch the
                  // next page of older rows. The two steps share one button.
                  if (statementRows.length > visibleLimit) setVisibleLimit((value) => value + 30);
                  else olderPages.fetchNextPage().then(() => setVisibleLimit((value) => value + 30));
                }}
              >
                <ChevronDown size={15} aria-hidden="true" />
                {olderPages.isFetching ? t('loading') : t('walletLoadMore')}
              </button>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'orders' ? (
          <div className="wallet-panel-body" data-wallet-recent-react="true">
            {walletQuery.isLoading ? <WalletState text={t('loading')} /> : null}
            {walletQuery.isError ? <WalletState text={t('walletRefreshFailed')} actionLabel={t('retry')} onAction={() => walletQuery.refetch()} /> : null}
            {!walletQuery.isLoading && !walletQuery.isError && recent.length === 0 ? <WalletState text={t('walletNoOrders')} /> : null}
            {recent.map((order) => {
              const recoverable = isUnpaidDeposit({ ...order, kind: order.kind }, Date.now() / 1000);
              return (
                <div className={`txrow-stack${recoverable ? ' has-recover' : ''}`} key={`${order.kind}:${order.id}`}>
                  <OrderRow order={order} onClick={() => openHistoryDetail(order)} />
                  {recoverable ? (
                    <button type="button" className="txrow-recover" data-order-recover={order.publicId} onClick={() => goToUnpaid(order.publicId)}>
                      {t('unpaidRecoverCta')}
                    </button>
                  ) : null}
                </div>
              );
            })}
            {!walletQuery.isLoading && !walletQuery.isError ? (
              <button className="wallet-load-more" type="button" data-wallet-view-all-react="true" onClick={() => setRoute('me.history')}>
                {t('walletViewAllHistory')}
              </button>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'tickets' ? (
          <div className="wallet-panel-body">
            <TicketsPanel
              data={raffleQuery.data}
              userId={user?.id}
              loading={raffleQuery.isLoading}
              error={raffleQuery.isError}
              nowSeconds={raffleNowSeconds}
              view={ticketsView}
              onViewChange={setTicketsView}
              onRefresh={() => { raffleQuery.refetch(); ticketLedgerQuery.refetch(); }}
              onFaq={() => setRoute('rewards')}
              onOpenRaffle={() => openWalletTicketsFlow('register', null, null)}
              onOpenParticipants={() => openWalletTicketsFlow('participants', null, null)}
              onOpenRecent={(event) => openWalletTicketsFlow('result', raffleEventRouteId(event), null)}
              ledger={ticketLedgerQuery.data}
              onOpenEventId={(eventId) => openWalletTicketsFlow('result', eventId, null)}
            />
          </div>
        ) : null}
      </section>

      {data?.cashierUnavailable ? <WalletState text={t('walletRefreshFailed')} actionLabel={t('retry')} onAction={() => walletQuery.refetch()} /> : null}

      {/* Sheets render at <body>, never inside this scrolling page — Mobile
          Safari scopes a fixed descendant of a scroller to that scroller, and
          the tab bar then paints over it. See SheetPortal. */}
      <SheetPortal>
      {depositOpen ? (
        <DepositSheet
          amount={depositAmount}
          channel={depositSettings}
          provider={depositChannel}
          order={depositOrder ? (orders.find((candidate) => candidate.id === depositOrder.id) || depositOrder) : null}
          payer={depositPayer}
          onPayerChange={setDepositPayer}
          canSubmit={canCreateDeposit && (!depositChannel?.requiresPayerIdentity || (depositPayer.name.trim().length >= 2 && /^\d{11}$/.test(depositPayer.cpf)))}
          pending={depositMutation.isPending}
          error={depositMutation.isError}
          minAmount={depositMin}
          maxAmount={depositMax}
          coupons={depositCoupons}
          couponsLoading={depositCouponsLoading}
          couponsError={depositQuoteAmount === depositAmount && couponsQuery.isError}
          offerBonus={depositOfferBonus}
          offerSelected={depositOfferSelected}
          onSelectOffer={(enabled)=>{setDepositOfferSelected(enabled);if(enabled)setSelectedCouponId(null);}}
          selectedCouponId={selectedCouponId}
          appliedBonus={appliedCouponBonus}
          onSelectCoupon={setSelectedCouponId}
          onAmountChange={(nextAmount, immediateQuote = false) => {
            setDepositAmount(nextAmount);
            setSelectedCouponId(null);
            if (immediateQuote && validCashierAmount(nextAmount, depositSettings) && nextAmount >= depositMin && nextAmount <= depositMax) {
              setDepositQuoteAmount(nextAmount);
            }
          }}
          onRetryCoupons={() => {
            if (canCreateDeposit) {
              setDepositQuoteAmount(depositAmount);
              void couponsQuery.refetch();
            }
          }}
          onSubmit={() => {
            if (!depositChannel) {
              showToast(t('walletDepositUnavailable').replace('%currency%', country.fiatCurrency), 'warn');
              void walletQuery.refetch();
              return;
            }
            const sc = depositCoupons.find((c) => c.id === selectedCouponId);
            setAppliedCouponBonus(depositOfferSelected ? depositOfferBonus : sc ? Number(sc.estimatedBonus || 0) : 0);
            prepareExternalDepositCheckout();
            depositMutation.mutate();
          }}
          onClose={closeDepositSheet}
          onSupport={() => { openSupportChatwoot({ user, language, route: 'wallet.deposit' }).catch(() => showToast(t('supportChatError'), 'warn')); }}
          onRecover={(publicId) => { closeDepositSheet(); goToUnpaid(publicId); }}
          onPoll={depositReturnMonitoring ? () => { void syncReturnedDeposit(); } : undefined}
          onRegenerate={() => {
            setEmbeddedCheckoutOpen(false);
            clearDepositTracking();
            setDepositOrder(null);
          }}
          embeddedCheckoutOpen={embeddedCheckoutOpen}
          onOpenEmbeddedCheckout={() => setEmbeddedCheckoutOpen(true)}
          onExitEmbeddedCheckout={exitEmbeddedDepositCheckout}
        />
      ) : null}
      {returnDialogOrder ? (
        <DepositReturnDialog
          order={returnDialogOrder}
          onLater={() => dismissReturnDialog(String(returnDialogOrder.publicId || ''))}
          onNotReceived={() => { const id = String(returnDialogOrder.publicId || ''); dismissReturnDialog(id); goToUnpaid(id); }}
        />
      ) : null}
      {withdrawOpen ? (
        <WithdrawSheet
          step={withdrawStep}
          payee={payee}
          payeeForm={payeeForm}
          channel={withdrawSettings}
          provider={withdrawChannel}
          balance={withdrawablePrincipal}
          eligibility={withdrawalQuote}
          abandonBonus={withdrawAbandonBonus}
          amount={withdrawAmount}
          fee={withdrawFee}
          net={withdrawNet}
          request={withdrawRequest}
          pending={withdrawMutation.isPending}
          error={withdrawMutation.isError}
          errorCode={withdrawMutation.error?.message}
          canSavePayee={canSavePayee(normalizePayee(payeeForm), withdrawChannel?.requiresPayoutDocument)}
          canContinue={canContinueWithdraw}
          canSubmit={canSubmitWithdraw}
          minAmount={withdrawMin}
          maxAmount={withdrawMax}
          onStepChange={setWithdrawStep}
          onPayeeFormChange={setPayeeForm}
          onAmountChange={(value) => {
            setWithdrawAmount(value);
            setWithdrawAbandonBonus(false);
          }}
          onAbandonBonusChange={setWithdrawAbandonBonus}
          onSavePayee={savePayee}
          onSubmit={() => {
            if (!withdrawChannel) {
              showToast(t('walletWithdrawUnavailable').replace('%currency%', country.fiatCurrency), 'warn');
              void walletQuery.refetch();
              return;
            }
            withdrawMutation.mutate();
          }}
          onClose={closeWithdrawSheet}
          onWithdrawalList={openWithdrawalList}
        />
      ) : null}
      </SheetPortal>
    </section>
  );
}

function WalletHeader({
  titleKey,
  eyebrowKey,
  refreshing,
  onRefresh,
}: {
  titleKey: MessageKey;
  eyebrowKey: MessageKey;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const setRoute = useAppStore((state) => state.setRoute);
  void refreshing; void onRefresh;
  return (
    <header className="tab-page-header" data-wallet-context-title-react={titleKey}>
      {/* Every wallet tab (Extrato / Transactions / Tickets) is reached from the
          lobby now that the header is unified, so back always means the lobby. */}
      <button className="icon-button" type="button" onClick={() => setRoute('lobby')} aria-label={t('backToLobby')}>
        <ArrowLeft size={20} aria-hidden="true" />
      </button>
      <div>
        <p className="eyebrow">{t(eyebrowKey)}</p>
        <h1>{t(titleKey)}</h1>
      </div>
    </header>
  );
}

/**
 * 62px ring: track + brand arc. arcPct has a 3% floor so a fresh deposit still shows a dot.
 * Only the drawn arc is decorative — the percentage stays readable text, so the
 * enclosing button's accessible name is "57% Rollover Bet R$ 858 more…". An
 * aria-label on that button would replace the whole sentence with the number.
 */
function UnlockRing({ pct, arcPct }: { pct: number; arcPct: number }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <span className="wallet-unlock-ring" data-pct={pct}>
      <svg width="62" height="62" viewBox="0 0 62 62" aria-hidden="true">
        <circle cx="31" cy="31" r={r} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="7" />
        <circle cx="31" cy="31" r={r} fill="none" stroke="var(--brand)" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - arcPct / 100)} transform="rotate(-90 31 31)" />
      </svg>
      <b>{pct}%</b>
    </span>
  );
}

function ChannelCard({ icon: Icon, title, channel }: { icon: LucideIcon; title: string; channel?: WalletPaymentChannel }) {
  const t = useI18nStore((state) => state.t);
  return (
    <article className="wallet-channel-card">
      <Icon size={18} aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <span>{channel ? channelRange(channel) : t('walletChannelUnavailable')}</span>
      </div>
    </article>
  );
}

function StatementRow({ row, meta }: { row: UiStatementRow; meta?: string }) {
  const t = useI18nStore((state) => state.t);
  const positive = row.amount > 0;
  const refLabel = txRefDisplay(row, t);
  return (
    <article className="wallet-tx-row">
      <span className={`wallet-row-icon ${row.type}`}>
        {row.type === 'deposit' ? <ArrowDownCircle size={17} aria-hidden="true" /> : row.type === 'withdraw' ? <ArrowUpCircle size={17} aria-hidden="true" /> : <ReceiptText size={17} aria-hidden="true" />}
      </span>
      <div>
        <strong>
          {txCategoryLabel(row.category, t)}
          {meta ? <em className="wallet-tx-meta">{meta}</em> : null}
        </strong>
        {/* A ledger is read to reconcile, and "1m" / "10h" cannot be matched
            against a bank statement or a support ticket. Same local
            MM/DD, HH:mm:ss the fairness screens use, so a player comparing two
            surfaces sees one format. */}
        <small>
          {refLabel ? <>{refLabel} · </> : null}
          <time dateTime={new Date(row.time * 1000).toISOString()}>{localDateTime(row.time)}</time>
        </small>
      </div>
      <div className="wallet-row-amount">
        <b className={positive ? 'win-text' : row.amount < 0 ? 'lose-text' : ''}>{row.amount < 0 ? '\u2212' : positive ? '+' : ''}{fmtMoney(Math.abs(row.amount))}</b>
        <small>{t('walletBalanceAfterShort')} {fmtMoney(row.balanceAfter)}</small>
      </div>
    </article>
  );
}

/**
 * One transaction in the records list.
 *
 * <p>Shaped after the My Bets row: the status lives in a 3px left edge bar
 * rather than a pill, the identity and the timestamp share a line, and values
 * are not wrapped in labelled boxes.
 *
 * <p>The row carries the headline only: kind, requested amount, status, time
 * and the full order id. Fee, net and what actually landed live in the detail
 * sheet the row opens — the list is for finding a record, the sheet for
 * reading it. The id is printed whole and may wrap, because a player quoting
 * it to support or matching it against a bank statement needs every character.
 */
function OrderRow({ order, onClick }: { order: UiOrder; onClick: () => void }) {
  const t = useI18nStore((state) => state.t);
  const status = orderStatusBucket(order.status, order.kind);
  const statusLabel = t(paymentStatusLabelKey(order.status, order.kind));
  const deposit = order.kind === 'deposit';
  const kindLabel = t(deposit ? 'transactionKindDeposit' : 'transactionKindWithdrawal');
  const KindIcon = deposit ? ArrowDownCircle : ArrowUpCircle;
  const stamp = localListTime(order.createdAt, Date.now() / 1000);
  const orderId = (order.publicId || order.id || '').trim();
  return (
    <button
      className={`txrow ${order.kind} is-${status}`}
      type="button"
      onClick={onClick}
      aria-label={`${kindLabel} ${fmtMoney(order.requestedAmount)} · ${statusLabel} · ${stamp}`}
    >
      <KindIcon className="txrow-kind" size={20} aria-hidden="true" />
      <span className="txrow-label">{kindLabel}</span>
      <span className="txrow-amount">{deposit ? '+' : '\u2212'}{fmtMoney(order.requestedAmount)}</span>
      <span className="txrow-meta">
        <time dateTime={order.createdAt ? new Date(order.createdAt * 1000).toISOString() : undefined}>{stamp}</time>
        {orderId ? <em>{orderId}</em> : null}
      </span>
      <span className="txrow-status">{statusLabel}</span>
    </button>
  );
}

function WalletEmpty({ filter, onDeposit }: { filter: WalletFilter; onDeposit?: () => void }) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className="wallet-empty">
      <Wallet size={26} aria-hidden="true" />
      <strong>{filter === 'all' ? t('walletEmptyTitle') : t('walletEmptyFiltered')}</strong>
      {filter === 'all' ? <p>{onDeposit ? t('walletEmptyBody') : t('walletChannelUnavailable')}</p> : null}
      {filter === 'all' && onDeposit ? (
        <button className="wallet-empty-cta" type="button" onClick={onDeposit}>
          <ArrowDownCircle size={16} aria-hidden="true" />
          {t('walletEmptyCta')}
        </button>
      ) : null}
    </div>
  );
}

function WalletState({ text, actionLabel, onAction }: { text: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="wallet-empty compact wallet-state-react">
      <span>{text}</span>
      {actionLabel && onAction ? (
        <button type="button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function TicketsPanel({
  data,
  userId,
  loading,
  error,
  nowSeconds,
  view,
  onViewChange,
  onRefresh,
  onFaq,
  onOpenRaffle,
  onOpenParticipants,
  onOpenRecent,
  ledger,
  onOpenEventId,
}: {
  data: RaffleData | undefined;
  userId: number | string | undefined;
  loading: boolean;
  error: boolean;
  nowSeconds: number;
  view: 'results' | 'tickets';
  onViewChange: (view: 'results' | 'tickets') => void;
  onRefresh: () => void;
  onFaq: () => void;
  onOpenRaffle: () => void;
  onOpenParticipants: () => void;
  onOpenRecent: (event: RaffleRecentEvent) => void;
  ledger: TicketLedgerResponse | undefined;
  onOpenEventId: (eventId: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const ledgerEntries = ledger?.entries || [];
  const recentEvents = (data?.recent_events || []).slice(0, 6);
  const totalWon = recentEvents.reduce((sum, event) => sum + Number(event.my_prize || 0), 0);

  if (loading) return <WalletState text={t('loading')} />;
  if (error) {
    return (
      <div className="wallet-empty compact">
        <AlertTriangle size={24} aria-hidden="true" />
        <strong>{t('walletTicketsLoadError')}</strong>
        <button className="ghost-action" type="button" onClick={onRefresh}>
          <RefreshCw size={15} aria-hidden="true" />
          {t('refresh')}
        </button>
      </div>
    );
  }

  return (
    <section className="tickets-panel">
      {/* No balance strip here: the tab above already carries the ticket count,
          and joining a draw belongs on the Draws page, not in the wallet. This
          tab is the record — what came in, what went out, how each draw ended.
          The way onward appears in the empty states, where the list itself has
          nothing to offer. */}
      <nav className="tickets-subtabs" aria-label={t('walletTabBilhetes')}>
        {/* A count of nothing is noise, and the tab above already drops its chip
            at zero — an empty list says "empty" better than a printed 0. */}
        <button type="button" className={view === 'results' ? 'active' : ''} onClick={() => onViewChange('results')}>
          {t('bilhetesTabResults')}
          {recentEvents.length > 0 ? <b>{recentEvents.length}</b> : null}
        </button>
        <button type="button" className={view === 'tickets' ? 'active' : ''} onClick={() => onViewChange('tickets')}>
          {t('bilhetesTabHistory')}
          {ledgerEntries.length > 0 ? <b>{ledgerEntries.length}</b> : null}
        </button>
      </nav>

      {view === 'results' ? (
        <div className="tickets-list">
          {totalWon > 0 ? (
            <div className="tickets-won-banner">
              <Sparkles size={16} aria-hidden="true" />
              <span>{t('bilhetesWonBanner').replace('%v%', fmtMoney(totalWon))}</span>
            </div>
          ) : null}
          {recentEvents.length === 0 ? (
            <WalletState text={t('bilhetesResultsEmpty')} actionLabel={t('bilhetesResultsEmptyCta')} onAction={onOpenRaffle} />
          ) : (
            recentEvents.map((event) => (
              <ResultRow
                key={String(event.event_id || event.id || event.scheduled_at)}
                event={event}
                nowSeconds={nowSeconds}
                onOpen={onOpenRecent}
              />
            ))
          )}
        </div>
      ) : (
        <div className="tickets-list">
          {ledgerEntries.length === 0 ? (
            <WalletState text={t('bilhetesHistoryEmpty')} actionLabel={t('bilhetesEarnCta')} onAction={onFaq} />
          ) : (
            ledgerEntries.map((entry, index) => (
              <TicketLedgerRow
                key={entry.id ?? `${entry.createdAt}-${index}`}
                entry={entry}
                onOpenEvent={onOpenEventId}
              />
            ))
          )}
        </div>
      )}
    </section>
  );
}

/**
 * One line of the ticket statement, read like a bank's: what happened, when to
 * the second, how many tickets moved (signed), and the balance it left behind.
 *
 * Spends and refunds name the draw they belong to and open it, so any ticket
 * can be followed to where it went and how that draw turned out.
 */
function TicketLedgerRow({
  entry,
  onOpenEvent,
}: {
  entry: TicketLedgerEntry;
  onOpenEvent: (eventId: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const kind = entry.kind || 'earn';
  const delta = Number(entry.delta || 0);
  const eventId = String(entry.eventId || '');
  const drawLabel = `${t('raffleRoundAt')} ${formatTicketTime(entry.eventScheduledAt)}`;
  // Spends and refunds are titled by the draw they belong to, so the two lines
  // for one cancelled draw sit under the same name. What separates them is the
  // sign and the "cancelled" note — not a longer title that ellipsizes away.
  const title = kind === 'earn' ? ticketSourceLabel(entry.source, t) : drawLabel;
  const Icon = kind === 'earn' ? Gift : kind === 'refund' ? RotateCcw : Ticket;
  const body = (
    <>
      <span className={`ticket-tx-icon is-${kind}`}>
        <Icon size={17} aria-hidden="true" />
      </span>
      <div className="ticket-tx-body">
        <strong>{title}</strong>
        <small>
          <time dateTime={new Date(Number(entry.createdAt || 0) * 1000).toISOString()}>
            {localDateTime(Number(entry.createdAt || 0))}
          </time>
          {kind === 'refund' ? ` · ${t('ticketTxRefundNote')}` : ''}
        </small>
      </div>
      <span className="ticket-tx-amounts">
        <b className={delta > 0 ? 'is-in' : 'is-out'}>
          {delta > 0 ? '+' : '−'}
          {fmtInt(Math.abs(delta))}
        </b>
        <small>{t('ticketTxBalanceAfter').replace('%n%', fmtInt(entry.balanceAfter))}</small>
      </span>
    </>
  );
  if (!eventId) return <article className="ticket-tx-row">{body}</article>;
  return (
    <button className="ticket-tx-row is-link" type="button" onClick={() => onOpenEvent(eventId)}>
      {body}
      <ChevronRight size={15} aria-hidden="true" className="ticket-tx-chevron" />
    </button>
  );
}

function ResultRow({ event, nowSeconds, onOpen }: { event: RaffleRecentEvent; nowSeconds: number; onOpen: (event: RaffleRecentEvent) => void }) {
  const t = useI18nStore((state) => state.t);
  const won = Number(event.my_prize || 0) > 0;
  const voided = event.status === 'voided';
  // A draw the player has tickets in but that has not been drawn yet. Without
  // this, spending tickets produced no record here at all: History said "used",
  // Results said nothing happened.
  const pending = event.status === 'registering' || event.status === 'locked' || event.status === 'settling';
  const open = event.status === 'registering';
  const day = formatRelativeDay(event.scheduled_at, nowSeconds, t);
  const myTickets = (event.my_entries || []).length;
  return (
    <button className={`result-row ${won ? 'is-won' : ''} ${pending ? 'is-pending' : ''}`} type="button" onClick={() => onOpen(event)}>
      <span className={`result-row-medal ${won ? 'is-won' : ''} ${pending ? 'is-pending' : ''}`}>
        {pending ? <Hourglass size={17} aria-hidden="true" /> : won ? <Trophy size={17} aria-hidden="true" /> : <CircleDot size={17} aria-hidden="true" />}
      </span>
      <div className="result-row-body">
        <strong>
          {t('raffleRoundAt')} {formatTicketTime(event.scheduled_at)}{' '}
          {day ? <em>· {day}</em> : null}
        </strong>
        <small>
          {/* Every row here is a draw the player entered, so their own stake is
              the useful number. The public entry total used to live here too and
              pushed the line to two rows on a 375px screen; it is one tap away
              inside the draw and does not earn the space. */}
          {myTickets > 0 ? (
            <b className="result-row-mine">
              {(myTickets === 1 ? t('bilhetesResultMyTicketsOne') : t('bilhetesResultMyTicketsMany')).replace('%n%', fmtInt(myTickets))}
            </b>
          ) : (
            <>{fmtInt(event.total_entries)} {t('raffleEntriesShort')}</>
          )}
        </small>
      </div>
      <span className="result-row-outcome">
        {voided ? (
          <em className="is-void">
            {(myTickets === 1 ? t('bilhetesResultRefundedOne') : t('bilhetesResultRefundedMany')).replace('%n%', fmtInt(myTickets))}
          </em>
        ) : pending ? (
          <em className="is-pending">
            {open ? t('bilhetesResultOpen') : t('bilhetesResultAwaiting')}
          </em>
        ) : won ? (
          <b className="is-won">{fmtMoney(event.my_prize)}</b>
        ) : (
          <em className="is-none">{t('raffleNoPrize')}</em>
        )}
      </span>
      <ChevronRight size={15} aria-hidden="true" className="result-row-chevron" />
    </button>
  );
}

export function RaffleRegisterSheet({
  data,
  userId,
  nowSeconds,
  onClose,
  onParticipants,
  onSubmit,
  onRules,
}: {
  data: RaffleData;
  userId: number | string | undefined;
  // Kept for API compat; the sheet now drives loading/error from local state.
  pending?: boolean;
  error?: boolean;
  nowSeconds: number;
  onClose: () => void;
  onParticipants: () => void;
  onSubmit: (count: number) => Promise<{ entries?: RaffleEntry[] } | void>;
  onRules: (targetId?: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const dialogRef = useRef<HTMLElement>(null);
  const confirmHeadingRef = useRef<HTMLHeadingElement>(null);
  const event = data.event;
  const activeTickets = ticketsForUser(data.tickets || [], userId).filter((ticket) => ticket.status === 'active');
  const myEntries = (data.entries || []).filter((entry) => sameId(entry.user_id, userId) && sameId(entry.event_id, eventId(event)));
  const maxCount = raffleAdditionalEntryCapacity({
    activeTickets: activeTickets.length,
    existingEntries: myEntries.length,
    totalEntries: Number(event?.total_entries || 0),
    maxTotalEntries: Number(event?.max_total_entries || 0),
    maxEntriesPerUser: Number(event?.max_entries_per_user || 20),
  });

  const [phase, setPhase] = useState<'pick' | 'confirmed'>('pick');
  const [count, setCount] = useState(Math.min(1, maxCount));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [confirmed, setConfirmed] = useState<{
    newSerials: number[];
    prevSerials: number[];
    used: number;
    activeAfter: number;
    totalAfter: number;
    minEntries: number;
  } | null>(null);

  // maxCount collapses "the draw sold out" and "you hit your own cap" into one
  // zero. They need different words: blaming the player for a full draw sends
  // them hunting for a limit they never hit.
  const eventSeats = Number(event?.max_total_entries || 0);
  const eventFull = eventSeats > 0 && Number(event?.total_entries || 0) >= eventSeats;

  const bracket = raffleBracket(event);
  const prizePool = raffleCashPool(bracket);
  const winnerCount = raffleWinnerCount(bracket);
  const totalEntries = Number(event?.total_entries || 0);
  const minEntries = Number(event?.min_entries_to_run || 0);
  const entriesRemaining = Math.max(0, minEntries - totalEntries);
  const minProgress = minEntries > 0 ? Math.max(0, Math.min(1, totalEntries / minEntries)) : 1;
  const raffleTitle = String(event?.display_name || '').trim() || t('raffleHourlyTitle');
  const hasEntries = myEntries.length > 0;
  const canSubmit = !!event && event.status === 'registering' && count > 0 && count <= maxCount && !submitting;

  // Quick-pick values: always 1 and Máx; two "nice" mids in between.
  const midCandidates = maxCount <= 6 ? [2, 3] : [3, 6, 10, 25];
  const midValues = midCandidates.filter((value) => value > 1 && value < maxCount).slice(0, 2);
  const quickValues = Array.from(new Set([1, ...midValues, maxCount])).filter((value) => value >= 1 && value <= maxCount);

  useEffect(() => {
    setCount((value) => (maxCount <= 0 ? 0 : Math.min(Math.max(value, 1), maxCount)));
  }, [maxCount]);

  // Move focus to the confirmation heading when entering the confirmed phase.
  useEffect(() => {
    if (phase === 'confirmed') confirmHeadingRef.current?.focus();
  }, [phase]);

  useModalLayer(dialogRef, onClose);

  const handleConfirm = async () => {
    if (!canSubmit) return;
    const prevSerials = myEntries.map((entry) => Number(entry.serial_no ?? -1)).filter((serial) => serial >= 0);
    const activeBefore = activeTickets.length;
    const totalBefore = totalEntries;
    const useCount = count;
    setSubmitting(true);
    setSubmitError(false);
    try {
      const result = await onSubmit(useCount);
      const returned = (result && typeof result === 'object' && 'entries' in result ? result.entries : undefined) ?? [];
      const returnedSerials = returned.map((entry) => Number(entry.serial_no ?? -1)).filter((serial) => serial >= 0);
      const prevSet = new Set(prevSerials);
      let newSerials = returnedSerials.filter((serial) => !prevSet.has(serial));
      // Backend may echo only the fresh entries (already all-new) — fall back to
      // the tail of what came back so numbers are always shown.
      if (newSerials.length === 0 && returnedSerials.length > 0) {
        newSerials = returnedSerials.slice(-useCount);
      }
      setConfirmed({
        newSerials,
        prevSerials,
        used: newSerials.length || useCount,
        activeAfter: Math.max(0, activeBefore - useCount),
        totalAfter: totalBefore + useCount,
        minEntries,
      });
      setPhase('confirmed');
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const backToPick = () => {
    setConfirmed(null);
    setSubmitError(false);
    setPhase('pick');
    setCount(maxCount <= 0 ? 0 : Math.min(1, maxCount));
  };

  // The full case never reaches this label — it gets its own footer action.
  const ctaLabel = submitting
    ? t('loading')
    : activeTickets.length === 0
      ? t('raffleNoTickets')
      : maxCount === 0
        ? t('raffleEntryLimitReached')
        : hasEntries
          ? t('raffleAddNumbersCta').replace('%n%', fmtInt(count))
          : t('raffleEnterNumbersCta').replace('%n%', fmtInt(count));

  const pickBody = (
    <>
      <div className="sheet-grabber" />
      <header className="sheet-header">
        <div>
          <h2>{raffleTitle}</h2>
          <p>{t('raffleDrawsIn')} <b>{formatRaffleCountdown(event?.scheduled_at, nowSeconds)}</b></p>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')} data-autofocus>×</button>
      </header>

      <div className="raffle-register-scroll">
        <div className="raffle-prize-pill">
          <span><b>{fmtMoney(prizePool)}</b> · {fmtInt(winnerCount)} {t('raffleWinnersShort')}</span>
        </div>

        {eventFull ? (
          /* The draw sold out — most often while this very sheet was open. A
             stepper pinned at 0 with an empty quick-pick row reads as a broken
             form, so state the outcome and where the tickets stand instead. */
          <section className="raffle-full-notice">
            <div>
              <Lock size={15} aria-hidden="true" />
              <strong>{t('raffleFullNoticeTitle')}</strong>
            </div>
            <p>{t('raffleFullNoticeBody')}</p>
          </section>
        ) : activeTickets.length > 0 ? (
          <section className="raffle-pick">
            <div className="raffle-pick-q">{t('raffleQtyQuestion')}</div>

            {/* One picker for every capacity. This used to switch to a slot-tile
                grid when ≤6 numbers remained — the same sheet changing shape
                mid-journey read as a different screen, so the stepper is now
                the only form (quick chips below still give one-tap picks). */}
            <div className="raffle-bigcount">
              <div className="raffle-bigcount-amt">
                <Ticket size={18} aria-hidden="true" />
                <b>{fmtInt(count)}</b>
                <span>{t('raffleReceiveNumbers').replace('%n%', '').trim()}</span>
              </div>
              <div className="raffle-bigcount-ctrl">
                <button type="button" aria-label="−" disabled={count <= 1 || submitting} onClick={() => setCount((value) => Math.max(1, value - 1))}>
                  <Minus size={16} aria-hidden="true" />
                </button>
                <button type="button" aria-label="+" disabled={count >= maxCount || submitting} onClick={() => setCount((value) => Math.min(maxCount, value + 1))}>
                  <Plus size={16} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="raffle-quick">
              {quickValues.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={count === value ? 'on' : ''}
                  disabled={submitting}
                  onClick={() => setCount(value)}
                >
                  {value === maxCount ? t('raffleQtyMax').replace('%n%', fmtInt(maxCount)) : fmtInt(value)}
                </button>
              ))}
            </div>

            <div className="raffle-math">
              <span>
                {t('raffleBalanceLine')
                  .split(/(%have%|%use%|%left%)/)
                  .map((chunk, index) => {
                    if (chunk === '%have%') return <b key={index}>{fmtInt(activeTickets.length)}</b>;
                    if (chunk === '%use%') return <b key={index} className="use">{fmtInt(count)}</b>;
                    if (chunk === '%left%') return <b key={index} className="left">{fmtInt(Math.max(0, activeTickets.length - count))}</b>;
                    return <span key={index}>{chunk}</span>;
                  })}
              </span>
              <button type="button" className="raffle-earn-link" onClick={() => onRules('rules-raffle-tickets')}>{t('raffleEarnMore')}</button>
            </div>
          </section>
        ) : (
          <section className="raffle-tickets-empty-react">
            <div>
              <Info size={15} aria-hidden="true" />
              <strong>{t('raffleTicketsEmptyTitle')}</strong>
            </div>
            <p>{t('raffleTicketsOriginHint')}</p>
            <button type="button" onClick={() => onRules('rules-raffle-tickets')}>
              <BookOpen size={15} aria-hidden="true" />
              {t('raffleTicketsEmptyCta')}
            </button>
          </section>
        )}

        {!eventFull && activeTickets.length > 0 ? (
          entriesRemaining > 0 ? (
            <div className="raffle-minrow">
              <AlertTriangle size={14} aria-hidden="true" />
              <span>{t('raffleMinRemaining').replace('%n%', fmtInt(entriesRemaining))}</span>
              <span className="raffle-minrow-bar"><i style={{ width: `${Math.round(minProgress * 100)}%` }} /></span>
            </div>
          ) : (
            <div className="raffle-minrow ok">
              <Check size={14} aria-hidden="true" />
              <span>{t('raffleMinReachedShort')}</span>
            </div>
          )
        ) : null}

        {!eventFull ? (
          <p className="raffle-irrev-line">
            <Info size={13} aria-hidden="true" />
            <span>{t('raffleConfirmIrrevShort')}</span>
          </p>
        ) : null}

        {submitError ? <p className="form-error" role="alert">{t('raffleRegisterError')}</p> : null}
      </div>

      <footer className="raffle-register-actions">
        {eventFull ? (
          /* A dead disabled button is a dead end. The one thing still worth
             doing here is looking at who did get in. */
          <button className="secondary-button sheet-submit is-signal" type="button" onClick={onParticipants}>
            <Users size={15} aria-hidden="true" />
            {t('raffleFullNoticeCta')}
          </button>
        ) : (
          <button className="primary-button sheet-submit raffle-confirm-button" type="button" disabled={!canSubmit || activeTickets.length === 0 || maxCount === 0} onClick={handleConfirm}>
            {ctaLabel}
          </button>
        )}
      </footer>
    </>
  );

  const confirmedBody = confirmed ? (() => {
    const isAddMore = confirmed.prevSerials.length > 0;
    const allSerials = [...confirmed.prevSerials, ...confirmed.newSerials];
    const totalOwned = allSerials.length;
    const newSet = new Set(confirmed.newSerials);
    const minReached = confirmed.minEntries <= 0 || confirmed.totalAfter >= confirmed.minEntries;
    const confProgress = confirmed.minEntries > 0 ? Math.max(0, Math.min(1, confirmed.totalAfter / confirmed.minEntries)) : 1;
    return (
      <>
        <div className="sheet-grabber" />
        <header className="raffle-confirmed-topbar">
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>

        <div className="raffle-register-scroll raffle-confirmed">
          <div className="raffle-conf-top">
            <span className="raffle-conf-check" aria-hidden="true"><Check size={15} strokeWidth={2.6} /></span>
            <h2 ref={confirmHeadingRef} tabIndex={-1}>
              {isAddMore
                ? t('raffleAddedTitle').replace('%n%', fmtInt(confirmed.newSerials.length))
                : t('raffleConfirmedTitle')}
            </h2>
          </div>

          <div className="raffle-nlabel">{t('raffleYourNumbersLabel')}</div>
          <div className="raffle-nums">
            {confirmed.newSerials.length > 0
              ? confirmed.newSerials.map((serial, index) => (
                <span key={`new-${serial}`} className="raffle-numstamp" style={{ animationDelay: `${index * 0.08}s` }}>
                  {ticketLabel(serial)}
                  {isAddMore ? <span className="raffle-numstamp-tag">{t('raffleNewTag')}</span> : null}
                </span>
              ))
              : <span className="raffle-numstamp">{t('raffleReceiveNumbers').replace('%n%', fmtInt(confirmed.used))}</span>}
          </div>

          {isAddMore ? (
            <>
              <div className="raffle-nlabel">{t('raffleAllYoursLabel').replace('%n%', fmtInt(totalOwned))}</div>
              <div className="raffle-nums">
                {allSerials.map((serial) => (
                  <span key={`all-${serial}`} className={`raffle-numstamp old${newSet.has(serial) ? ' is-new' : ''}`}>
                    {ticketLabel(serial)}
                  </span>
                ))}
              </div>
            </>
          ) : null}

          <span className="raffle-balchip">
            {t('raffleBalanceAfter')
              .split(/(%use%|%left%)/)
              .map((chunk, index) => {
                if (chunk === '%use%') return <b key={index} className="u">{fmtInt(confirmed.used)}</b>;
                if (chunk === '%left%') return <b key={index} className="l">{fmtInt(confirmed.activeAfter)}</b>;
                return <span key={index}>{chunk}</span>;
              })}
          </span>

          {minReached ? (
            <div className="raffle-minrow ok" style={{ width: '100%' }}>
              <Check size={14} aria-hidden="true" />
              <span>{t('raffleMinConfirmed')}</span>
            </div>
          ) : (
            <div className="raffle-minrow" style={{ width: '100%' }}>
              <AlertTriangle size={14} aria-hidden="true" />
              <span>{t('raffleMinPending').replace('%cur%', fmtInt(confirmed.totalAfter)).replace('%min%', fmtInt(confirmed.minEntries))}</span>
              <span className="raffle-minrow-bar"><i style={{ width: `${Math.round(confProgress * 100)}%` }} /></span>
            </div>
          )}

          <button type="button" className="raffle-publicline" onClick={() => onRules()}>
            <ShieldCheck size={13} aria-hidden="true" />
            {t('rafflePublicNumber')}
          </button>

          <div className="raffle-next">
            <button type="button" className="raffle-btn raffle-btn-gold" onClick={onParticipants}>{t('raffleWatchCta')}</button>
            {confirmed.activeAfter > 0 ? (
              <button type="button" className="raffle-btn raffle-btn-cyan" onClick={backToPick}>{t('raffleAddMoreCta')}</button>
            ) : (
              <button type="button" className="raffle-btn raffle-btn-cyan" onClick={() => onRules('rules-raffle-tickets')}>{t('raffleEarnMore')}</button>
            )}
            <button type="button" className="raffle-btn raffle-btn-ghost" onClick={onClose}>{t('raffleCloseSheet')}</button>
          </div>
        </div>
      </>
    );
  })() : null;

  const sheet = (
    <div className="sheet-backdrop raffle-register-backdrop" role="presentation" onMouseDown={onClose}>
      <section ref={dialogRef} className="profile-sheet raffle-register-sheet-react" role="dialog" aria-modal="true" aria-label={raffleTitle} onMouseDown={(event) => event.stopPropagation()}>
        {phase === 'confirmed' && confirmedBody ? confirmedBody : pickBody}
      </section>
    </div>
  );

  return typeof document === 'undefined' ? sheet : createPortal(sheet, document.body);
}

function useModalLayer(dialogRef: React.RefObject<HTMLElement | null>, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const dialogElement = dialog;

    const appRoot = document.getElementById('root');
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    const rootWasInert = appRoot?.inert ?? false;
    const rootAriaHidden = appRoot?.getAttribute('aria-hidden');

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    if (appRoot) {
      appRoot.inert = true;
      appRoot.setAttribute('aria-hidden', 'true');
    }

    const focusableSelector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const initialFocus = dialogElement.querySelector<HTMLElement>('[data-autofocus]')
      || dialogElement.querySelector<HTMLElement>(focusableSelector)
      || dialogElement;
    if (!dialogElement.hasAttribute('tabindex')) dialogElement.tabIndex = -1;
    window.requestAnimationFrame(() => initialFocus.focus());

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogElement.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) {
        event.preventDefault();
        dialogElement.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    dialogElement.addEventListener('keydown', handleKeyDown);
    return () => {
      dialogElement.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
      if (appRoot) {
        appRoot.inert = rootWasInert;
        if (rootAriaHidden == null) appRoot.removeAttribute('aria-hidden');
        else appRoot.setAttribute('aria-hidden', rootAriaHidden);
      }
      window.requestAnimationFrame(() => previousFocus?.focus());
    };
  }, [dialogRef]);
}

export function RaffleParticipantsSheet({
  data,
  userId,
  onBack,
  onOpenProfile,
  guestFooter,
  guest,
  onClose,
}: {
  data: RaffleData;
  userId: number | string | undefined;
  onBack?: () => void;
  onOpenProfile: (userId: number | string) => void;
  guestFooter?: ReactNode;
  guest?: boolean;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'mine'>('all');
  const dialogRef = useRef<HTMLElement | null>(null);
  useModalLayer(dialogRef, onClose);
  const event = data.event;
  const entries = (data.entries || [])
    .filter((entry) => sameId(entry.event_id, eventId(event)))
    .sort((a, b) => Number(a.serial_no || 0) - Number(b.serial_no || 0));
  const myEntries = entries.filter((entry) => sameId(entry.user_id, userId));
  const participantCount = new Set(entries.map((entry) => String(entry.user_id || ''))).size;
  const filtered = entries.filter((entry) => {
    if (scope === 'mine' && !sameId(entry.user_id, userId)) return false;
    const rawValue = search.trim().toLowerCase();
    if (!rawValue) return true;
    // 票号不再带 `#`，所以不能再 `.slice(1)` —— 那会切掉第一位数字，搜 032
    // 变成搜 32。输入里的 `#` 还是要容忍：玩家会照着以前的习惯打。
    const serialValue = rawValue.replace(/^#/, '');
    const usernameValue = rawValue.replace(/^@/, '');
    return ticketLabel(entry.serial_no).includes(serialValue)
      || String(entry.username || '').toLowerCase().includes(usernameValue);
  });

  const sheet = (
    <div className="sheet-backdrop raffle-participants-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="profile-sheet raffle-participants-sheet-react"
        role="dialog"
        aria-modal="true"
        aria-labelledby="raffle-entries-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-grabber" />
        <header className="raffle-participants-header">
          {onBack ? (
            <button className="icon-button" type="button" onClick={onBack} aria-label={t('back')}>
              <ArrowLeft size={19} aria-hidden="true" />
            </button>
          ) : <span aria-hidden="true" />}
          <div>
            <h2 id="raffle-entries-title">{t('raffleParticipantsList')}</h2>
            <p>{t('raffleRoundAt')} {formatTicketTime(event?.scheduled_at)}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={19} aria-hidden="true" />
          </button>
        </header>
        <div className="raffle-participants-body">
          <section className="raffle-participants-overview" aria-label={t('raffleEntriesOverview')}>
            <strong>
              {t('raffleParticipantsSummary')
                .replace('%entries%', fmtInt(entries.length))
                .replace('%players%', fmtInt(participantCount))}
            </strong>
            <span>{t('rafflePublicLedger')}</span>
          </section>

          {myEntries.length > 0 ? (
            <section className="raffle-my-entries">
              <div>
                <span>{t('raffleYourEntries')}</span>
                <b>{fmtInt(myEntries.length)}</b>
              </div>
              <div className="raffle-my-entry-numbers" aria-label={t('raffleYourEntries')}>
                {myEntries.map((entry) => <span key={`mine-${entry.entry_id || entry.serial_no}`}>{ticketLabel(entry.serial_no)}</span>)}
              </div>
            </section>
          ) : null}

          <div className="raffle-entry-controls">
            <div className="raffle-entry-scope" role="tablist" aria-label={t('raffleEntriesFilter')}>
              <button type="button" role="tab" aria-selected={scope === 'all'} className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>
                {t('raffleEntriesAll')} <b>{fmtInt(entries.length)}</b>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={scope === 'mine'}
                className={scope === 'mine' ? 'active' : ''}
                disabled={myEntries.length === 0}
                onClick={() => setScope('mine')}
              >
                {t('raffleEntriesMine')} <b>{fmtInt(myEntries.length)}</b>
              </button>
            </div>
            <label className="raffle-entry-search">
              <Search size={17} aria-hidden="true" />
              <input
                value={search}
                inputMode="search"
                aria-label={t('raffleParticipantsSearch')}
                placeholder={t('raffleParticipantsSearch')}
                onChange={(event) => setSearch(event.target.value)}
              />
              {search ? (
                <button type="button" onClick={() => setSearch('')} aria-label={t('clear')}>
                  <X size={15} aria-hidden="true" />
                </button>
              ) : null}
            </label>
          </div>

          <div className="raffle-entry-list-heading" aria-live="polite">
            <span>{scope === 'mine' ? t('raffleEntriesMine') : t('raffleEntriesAll')}</span>
            <b>{t('raffleSearchResults').replace('%n%', fmtInt(filtered.length))}</b>
          </div>

          <div className="raffle-participants-list">
            {filtered.length === 0 ? <div className="raffle-entry-empty">{t('raffleParticipantsEmpty')}</div> : null}
            {filtered.map((entry) => {
              const username = String(entry.username || '').trim() || t('raffleUnknownPlayer');
              const isMine = sameId(entry.user_id, userId);
              const canOpenProfile = entry.user_id !== undefined && entry.user_id !== null && username !== t('raffleUnknownPlayer');
              return (
                <button
                  className={`raffle-participant-row-react ${isMine ? 'mine' : ''}`}
                  type="button"
                  disabled={!canOpenProfile}
                  onClick={() => {
                    if (canOpenProfile) onOpenProfile(entry.user_id as number | string);
                  }}
                  aria-label={canOpenProfile
                    ? (guest ? username : t('raffleOpenProfile').replace('%username%', username))
                    : username}
                  key={`${entry.entry_id || entry.serial_no}`}
                >
                  <b>{ticketLabel(entry.serial_no)}</b>
                  <RaffleAvatar user={entry} mine={isMine} letter={username === t('raffleUnknownPlayer') ? '' : username.slice(0, 1).toUpperCase()} />
                  <span className="raffle-entry-user">
                    <strong>@{username}</strong>
                    {isMine ? <em>{t('raffleYou')}</em> : null}
                    <small>{t('raffleEnteredAt').replace('%time%', formatTicketTimeSec(entry.registered_at))}</small>
                  </span>
                  {canOpenProfile ? <ChevronRight size={18} aria-hidden="true" /> : null}
                </button>
              );
            })}
          </div>
          {guestFooter}
        </div>
      </section>
    </div>
  );

  return typeof document === 'undefined' ? sheet : createPortal(sheet, document.body);
}

// Small avatar bubble for raffle lists (winners / entries / participants).
// Renders the player's preset-pack WebP or thumb URL inside the existing
// letter circle; the letter stays as the fallback (no avatar, or load error).
// Perf: images are 26px, browser-lazy (only on-screen rows fetch), decode off
// the main thread, and repeated entries by the same user hit the HTTP cache.
function RaffleAvatar({ user, mine, letter }: {
  user: { avatar_thumb?: string; avatar_default_id?: number } | null | undefined;
  mine?: boolean;
  letter?: string;
}) {
  const [broken, setBroken] = useState(false);
  const url = broken ? '' : resolveAvatarUrl(user || undefined);
  const fallback = mine ? '★' : (letter || '·');
  return (
    <span className={`winner-avatar ${mine ? 'is-mine' : ''} ${!mine && !letter && !url ? 'is-anon' : ''}`.trim()} aria-hidden="true">
      {url
        ? <img src={url} alt="" loading="lazy" decoding="async" width={26} height={26} onError={() => setBroken(true)} />
        : fallback}
    </span>
  );
}

export function RaffleResultDetailSheet({
  event,
  userId,
  reveal = false,
  onClose,
  onTickets,
  onRules,
  onVerifyAnchor,
  onOpenProfile,
  guestFooter,
  guest,
}: {
  event: RaffleRecentEvent;
  userId: number | string | undefined;
  // The winner-reveal animation is a "live draw" moment. Reviewing a past result
  // (from Wallet, notifications, the recap) should go straight to the outcome —
  // opt in only where a draw is genuinely resolving in front of the user.
  reveal?: boolean;
  onClose: () => void;
  onTickets: () => void;
  onRules: () => void;
  onVerifyAnchor: (roundNumber: string) => void;
  onOpenProfile: (userId: number | string) => void;
  guestFooter?: ReactNode;
  guest?: boolean;
}) {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  const winners = event.winners || [];
  const myEntries = event.my_entries || [];
  const myEntryIds = new Set(myEntries.map((entry) => String(entry.entry_id || '')).filter(Boolean));
  const mySerials = new Set(myEntries.map((entry) => ticketSerial(entry.serial_no)));
  const myWinnings = winners.filter((winner) => isMyRaffleWinner(winner, userId, myEntryIds, mySerials));
  const myPrize = Number(event.my_prize || myWinnings.reduce((sum, winner) => sum + Number(winner.amount || 0), 0));
  const totalEntries = Number(event.total_entries || 0);
  const serialBase = event.serial_base === 1 ? 1 : 0;
  const maxEntries = Number(event.max_total_entries || 0);
  const meterPct = maxEntries > 0 ? Math.min(100, Math.round((totalEntries / maxEntries) * 100)) : 0;
  const isFullHouse = maxEntries > 0 && totalEntries >= maxEntries;
  const digits = String(event.d_digits || '').replace(/\D/g, '');
  const voided = event.status === 'voided';
  const settled = event.status === 'settled';
  const anchorRound = String(event.anchor_round_number ?? '').trim();
  const firstWinnerSerial = Number(winners[0]?.serial_no ?? -1);
  const won = myPrize > 0;
  const hasEntries = myEntries.length > 0;
  // Derived when no winner record exists, so a draw that resolved without a
  // recorded winner still states which number came up -- otherwise the sheet
  // says "your numbers did not come up" and never shows the one they lost to.
  const drawnSerial = drawnSerialFrom(winners[0]?.serial_no, digits, totalEntries, serialBase);
  const minEntries = Number(event.min_entries_to_run || 0);
  const calcLines = raffleVerifyStep1Text(digits, totalEntries, serialBase, t);
  // Per-entry hit map: an entry "hit" when a winner shares its drawn serial.
  const entryTiles = myEntries.map((entry) => {
    const win = winners.find((winner) => ticketSerial(winner.serial_no) === ticketSerial(entry.serial_no));
    return {
      key: String(entry.entry_id || entry.serial_no),
      label: ticketLabel(entry.serial_no),
      hit: Boolean(win),
      amount: Number(win?.amount || 0),
    };
  });
  const hitCount = entryTiles.filter((tile) => tile.hit).length;

  // Identity line: the same "#seq · Draw date · global #N" the archive detail
  // The anchor is the round that ENDS at scheduled_at (see raffleAnchorRound);
  // its date is the gaming day the draw belongs to, which is not always the
  // device's day. Tapping "Verify this round" lands on a header that reads
  // exactly like this line.
  const anchorLike = raffleAnchorRound(event.scheduled_at);
  const drawDate = anchorLike ? roundDateFromRound(anchorLike) : '';
  // 期号不进这一行（2026-09-18 PM）：它和票号都是「#三位数」，同屏出现没人
  // 分得清。一期由日期 + 开奖时刻认（头部那行已经写了时刻），要一个可引用的
  // 技术号时用 global 号 —— 客服和验算页要的就是它。
  const identityLine = [
    drawDate ? `${t('roundDrawDay')} ${brDayLabel(drawDate)}` : '',
    anchorRound ? t('roundGlobalNumber').replace('%n%', String(anchorRound)) : '',
  ].filter(Boolean).join(' · ');

  const eventKey = String(event.event_id || event.id || event.scheduled_at || digits || 'raffle');
  const prefersReducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shouldReveal = reveal && settled && digits.length > 0 && winners.length > 0 && !prefersReducedMotion;
  const [revealing, setRevealing] = useState(shouldReveal);
  const [showFair, setShowFair] = useState(false);
  const [peopleTab, setPeopleTab] = useState<null | 'winners' | 'entries'>(null);
  // Full public entry list, in serial order — the tangible "N" of the draw
  // formula. Lets anyone count the field and find their own serial in it.
  const allEntries = useMemo(
    () => (event.entries || []).slice().sort((a, b) => ticketSerial(a.serial_no) - ticketSerial(b.serial_no)),
    [event.entries],
  );

  useEffect(() => {
    setRevealing(shouldReveal);
    if (!shouldReveal) return undefined;
    const timer = window.setTimeout(() => setRevealing(false), 2100);
    return () => window.clearTimeout(timer);
  }, [eventKey, shouldReveal]);

  const digitCells = (digits || '----------').slice(0, 10).padEnd(10, '-').split('');

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet raffle-result-sheet-react" role="dialog" aria-modal="true" aria-label={t('raffleResultTitle')} onMouseDown={(event) => event.stopPropagation()} data-raffle-reveal-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('raffleResultTitle')}</h2>
            <p>{t('raffleRoundAt')} {formatTicketTime(event.scheduled_at)} · {fmtInt(totalEntries)} {t('raffleEntriesShort')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>
        {identityLine ? <p className="raffle-result-ident">{identityLine}</p> : null}

        {revealing ? (
          <RaffleRevealStage
            digits={digits}
            firstWinnerSerial={firstWinnerSerial}
            totalEntries={totalEntries}
            winnerCount={winners.length}
            onSkip={() => setRevealing(false)}
          />
        ) : (
          <>
            <div className="raffle-result-body">
              {won ? (
                <section className="raffle-result-hero is-win">
                  <BrandOwl size={44} className="raffle-result-owl" />
                  <span className="raffle-result-hero-label">{t('raffleWonTitle')}</span>
                  <strong className="raffle-result-amount">{fmtMoney(myPrize)}</strong>
                  <span className="raffle-result-credit"><i aria-hidden="true" />{t(marketCopyKey('raffleCreditedWallet', countryCode))}</span>
                </section>
              ) : voided ? (
                <section className="raffle-result-hero is-void">
                  <span className="raffle-result-hero-label">{t('raffleVoidedTitle')}</span>
                  <span className="raffle-result-hero-msg">
                    {minEntries > 0
                      ? t('raffleVoidedReasonMin').replace('%n%', fmtInt(minEntries))
                      : t('raffleVoidedReasonGeneric')}
                  </span>
                  {hasEntries ? (
                    <span className="raffle-result-refund"><Check size={13} aria-hidden="true" />{t('raffleTicketsReturned').replace('%n%', fmtInt(myEntries.length))}</span>
                  ) : null}
                </section>
              ) : hasEntries ? (
                <section className="raffle-result-hero is-lose">
                  <BrandOwl size={44} className="raffle-result-owl" />
                  <span className="raffle-result-hero-label">{t('raffleNotThisTime')}</span>
                  <span className="raffle-result-hero-msg">{t('raffleNotWonMsg').replace('%n%', fmtInt(myEntries.length))}</span>
                </section>
              ) : null}

              {voided ? (
                <p className="raffle-result-void-hint">{t('raffleTicketsReturnedHint')}</p>
              ) : null}

              {/* Drawn number and the user's own numbers share one card: the
                  only question this screen answers is how those two compare,
                  and that reads worse across a gap than side by side. */}
              {!voided && (drawnSerial !== null || hasEntries) ? (
                <section className="raffle-result-nums">
                  {drawnSerial !== null ? (
                    <div className="raffle-result-drawn">
                      <span className="raffle-result-drawn-label">{t('raffleDrawnNumber')}</span>
                      {/* drawnSerial 已经是最终号码（drawnSerialFrom 负责换算），
                          走 ticketNo 不再 +1 —— 以前这里叠了第二次 +1，线上给
                          0 号时把第 1 号印成了 002。 */}
                      <span className="raffle-result-drawn-no">{ticketLabel(drawnSerial)}</span>
                    </div>
                  ) : null}

                  {hasEntries ? (
                    <div className="raffle-result-mynums">
                      <div className="raffle-result-mynums-head">
                        <span className="raffle-result-sectlabel">{t('raffleYourNumbersLabel')}</span>
                        {hitCount > 0 ? (
                          <span className="raffle-result-mynums-sum">
                            <Trophy size={13} aria-hidden="true" />
                            {t('raffleNumbersWonSummary')
                              .replace('%h%', fmtInt(hitCount))
                              .replace('%m%', fmtInt(entryTiles.length))
                              .replace('%v%', fmtMoney(myPrize))}
                          </span>
                        ) : (
                          <span className="raffle-result-mynums-count">{fmtInt(entryTiles.length)}</span>
                        )}
                      </div>
                      <div className="raffle-result-mynums-row">
                        {entryTiles.map((tile) => (
                          <span key={tile.key} className={`raffle-result-tile ${tile.hit ? 'is-hit' : 'is-miss'}`}>
                            <b>{tile.label}</b>
                            {/* A miss carries no second line. The em-dash it used to
                                print doubled every tile's height for no information,
                                and six of them pushed the rest of the sheet under
                                the fold. */}
                            {tile.hit ? <em>{t('raffleHitMark').replace('%v%', fmtMoney(tile.amount))}</em> : null}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {!voided && digits.length > 0 ? (
                showFair ? (
                  <section className="raffle-result-fair">
                    <div className="raffle-result-fair-head">
                      <ShieldCheck size={15} aria-hidden="true" />
                      {t('raffleFairRow')}
                    </div>
                    <p className="raffle-result-fair-intro">{t('raffleFairIntro')}</p>
                    <div className="raffle-result-dgrid" aria-label={t('raffleFairRow')}>
                      {digitCells.map((digit, index) => (
                        <span className="raffle-result-dcell" key={`d-${index}`}>
                          <small>D{index + 1}</small>
                          <b>{digit}</b>
                        </span>
                      ))}
                    </div>
                    {calcLines.length > 0 ? (
                      <div className="raffle-result-calc">
                        {calcLines.map((line) => <div key={line}>{line}</div>)}
                      </div>
                    ) : null}
                    {anchorRound ? (
                      <button type="button" className="raffle-result-verify" onClick={() => onVerifyAnchor(anchorRound)}>
                        <ExternalLink size={14} aria-hidden="true" />
                        {t('raffleVerifyRound')}
                      </button>
                    ) : null}
                    <button type="button" className="raffle-result-howlink" onClick={onRules}>{t('raffleHowItWorksLink')}</button>
                  </section>
                ) : (
                  <button type="button" className="raffle-result-row" aria-expanded={false} onClick={() => setShowFair(true)}>
                    <ShieldCheck size={17} aria-hidden="true" className="raffle-result-row-ic" />
                    <span><b>{t('raffleFairRow')}</b></span>
                    <ChevronRight size={16} aria-hidden="true" className="raffle-result-row-cv" />
                  </button>
                )
              ) : null}

              {/* One door to the people of this draw: entry meter (how full it got)
                  on the row itself, winners + full entry list behind it, in tabs. */}
              {!voided && (winners.length > 0 || allEntries.length > 0) ? (
                <section className="raffle-result-people">
                  <button
                    type="button"
                    className="raffle-result-row raffle-result-meter-row"
                    aria-expanded={peopleTab != null}
                    onClick={() => setPeopleTab(peopleTab ? null : (winners.length > 0 ? 'winners' : 'entries'))}
                  >
                    <Users size={17} aria-hidden="true" className="raffle-result-row-ic" />
                    <span className="rr-meter">
                      <span className="rr-meter-top">
                        <b>{t('sorteiosEntriesLabel')}</b>
                        <em>
                          {maxEntries > 0 ? `${fmtInt(totalEntries)} / ${fmtInt(maxEntries)}` : fmtInt(totalEntries)}
                          {isFullHouse ? <i className="rr-full">{t('raffleEntriesFull')}</i> : null}
                        </em>
                      </span>
                      {maxEntries > 0 ? (
                        <span className="rr-meter-bar"><i style={{ width: `${Math.max(4, meterPct)}%` }} /></span>
                      ) : null}
                    </span>
                    {peopleTab
                      ? <ChevronDown size={16} aria-hidden="true" className="raffle-result-row-cv" />
                      : <ChevronRight size={16} aria-hidden="true" className="raffle-result-row-cv" />}
                  </button>
                  {peopleTab ? (
                    <>
                      <div className="rr-people-tabs" role="tablist">
                        <button type="button" role="tab" aria-selected={peopleTab === 'winners'} className={peopleTab === 'winners' ? 'active' : ''} disabled={winners.length === 0} onClick={() => setPeopleTab('winners')}>
                          {t('raffleTabWinners')} <b>{fmtInt(winners.length)}</b>
                        </button>
                        <button type="button" role="tab" aria-selected={peopleTab === 'entries'} className={peopleTab === 'entries' ? 'active' : ''} disabled={allEntries.length === 0} onClick={() => setPeopleTab('entries')}>
                          {t('raffleTabEntries')} <b>{fmtInt(allEntries.length)}</b>
                        </button>
                      </div>
                      {peopleTab === 'winners' ? (
                    <ol className="raffle-result-winner-list">
                      {winners.map((winner) => {
                        const mine = isMyRaffleWinner(winner, userId, myEntryIds, mySerials);
                        const username = String(winner.username || '').trim();
                        const canOpenProfile = !mine && winner.user_id != null && username.length > 0;
                        const nameNode = mine ? (
                          <em className="winner-you">{t('raffleYou')}</em>
                        ) : username ? (
                          <em className="winner-name">@{username}</em>
                        ) : (
                          <em className="winner-name is-anon">{t('raffleUnknownPlayer')}</em>
                        );
                        return (
                          <li
                            className={`${mine ? 'is-mine' : ''} ${canOpenProfile ? 'is-link' : ''}`.trim()}
                            key={`${winner.winner_id || winner.serial_no}`}
                          >
                            {canOpenProfile ? (
                              <button
                                type="button"
                                className="raffle-result-winner-row"
                                onClick={() => onOpenProfile(winner.user_id as number | string)}
                                aria-label={guest ? username : t('raffleOpenProfile').replace('%username%', username)}
                              >
                                <b>{winner.rank}°</b>
                                <span>{ticketLabel(winner.serial_no)}</span>
                                <RaffleAvatar user={winner} letter={username.slice(0, 1).toUpperCase()} />
                                {nameNode}
                                <strong>{fmtMoney(winner.amount)}</strong>
                                <ChevronRight size={15} aria-hidden="true" className="winner-chevron" />
                              </button>
                            ) : (
                              <div className="raffle-result-winner-row is-static">
                                <b>{winner.rank}°</b>
                                <span>{ticketLabel(winner.serial_no)}</span>
                                <RaffleAvatar user={winner} mine={mine} letter={username.slice(0, 1).toUpperCase()} />
                                {nameNode}
                                <strong>{fmtMoney(winner.amount)}</strong>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                      ) : (
                    <ol className="raffle-result-winner-list raffle-result-entry-list">
                      {allEntries.map((entry) => {
                        const mine = myEntryIds.has(String(entry.entry_id || '')) || (userId != null && String(entry.user_id ?? '') === String(userId));
                        const username = String(entry.username || '').trim();
                        const canOpenProfile = !mine && entry.user_id != null && username.length > 0;
                        const nameNode = mine ? (
                          <em className="winner-you">{t('raffleYou')}</em>
                        ) : username ? (
                          <em className="winner-name">@{username}</em>
                        ) : (
                          <em className="winner-name is-anon">{t('raffleUnknownPlayer')}</em>
                        );
                        const timeNode = <time className="entry-time">{formatTicketTimeSec(entry.registered_at)}</time>;
                        return (
                          <li
                            className={`${mine ? 'is-mine' : ''} ${canOpenProfile ? 'is-link' : ''}`.trim()}
                            key={`${entry.entry_id || entry.serial_no}`}
                          >
                            {canOpenProfile ? (
                              <button
                                type="button"
                                className="raffle-result-winner-row"
                                onClick={() => onOpenProfile(entry.user_id as number | string)}
                                aria-label={guest ? username : t('raffleOpenProfile').replace('%username%', username)}
                              >
                                <span>{ticketLabel(entry.serial_no)}</span>
                                <RaffleAvatar user={entry} letter={username.slice(0, 1).toUpperCase()} />
                                {nameNode}
                                {timeNode}
                                <ChevronRight size={15} aria-hidden="true" className="winner-chevron" />
                              </button>
                            ) : (
                              <div className="raffle-result-winner-row is-static">
                                <span>{ticketLabel(entry.serial_no)}</span>
                                <RaffleAvatar user={entry} mine={mine} letter={username.slice(0, 1).toUpperCase()} />
                                {nameNode}
                                {timeNode}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                      )}
                    </>
                  ) : null}
                </section>
              ) : null}
              {guestFooter}
            </div>

            <div className="raffle-result-foot">
              {voided ? (
                <>
                  <button type="button" className="raffle-result-cta is-gold" onClick={onClose}>{t('raffleNextDraw')}</button>
                  <button type="button" className="raffle-result-cta is-ghost" onClick={onClose}>{t('raffleCloseSheet')}</button>
                </>
              ) : !won && hasEntries ? (
                <>
                  <button type="button" className="raffle-result-cta is-gold" onClick={onClose}>{t('raffleNextDraw')}</button>
                  <button type="button" className="raffle-result-cta is-cyan" onClick={onTickets}>{t('raffleEarnMore')}</button>
                </>
              ) : (
                <button type="button" className="raffle-result-cta is-gold" onClick={onClose}>{t('raffleContinue')}</button>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function RaffleRevealStage({
  digits,
  firstWinnerSerial,
  totalEntries,
  winnerCount,
  onSkip,
}: {
  digits: string;
  firstWinnerSerial: number;
  totalEntries: number;
  winnerCount: number;
  onSkip: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const digitList = (digits || '0000000000').slice(0, 10).padEnd(10, '0').split('');
  return (
    <section className="raffle-reveal-stage-react" data-raffle-reveal-stage-react="true">
      <div className="raffle-reveal-orbit-react" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <div className="raffle-reveal-copy-react">
        <span>{t('raffleRevealDrawing')}</span>
        <strong>{firstWinnerSerial >= 0 ? ticketLabel(firstWinnerSerial) : '--'}</strong>
        <p>{t('raffleRevealFirstSerial').replace('%serial%', firstWinnerSerial >= 0 ? ticketLabel(firstWinnerSerial) : '--')}</p>
      </div>
      <div className="raffle-reveal-digits-react" aria-label={t('raffleSeed')}>
        {digitList.map((digit, index) => (
          <b key={`${digit}:${index}`} style={{ '--i': index } as CSSProperties}>
            <small>D{index + 1}</small>
            {digit}
          </b>
        ))}
      </div>
      <div className="raffle-reveal-formula-react">
        <span>{t('raffleRevealFormula').replace('%n%', fmtInt(totalEntries || 0)).replace('%k%', fmtInt(winnerCount || 0))}</span>
      </div>
      <button type="button" onClick={onSkip}>{t('raffleViewFullResult')}</button>
    </section>
  );
}

function isMyRaffleWinner(
  winner: RaffleWinner,
  userId: number | string | undefined,
  myEntryIds: Set<string>,
  mySerials: Set<number>,
) {
  return myEntryIds.has(String(winner.entry_id || ''))
    || mySerials.has(ticketSerial(winner.serial_no))
    || sameId(winner.user_id, userId);
}

function couponName(c: DepositCoupon, lang: string) {
  const n = c.nameI18n || {};
  const raw = n[lang] || n['pt-BR'] || n['en'] || c.code;
  // The name is free text and the payout is not. Any percentage written into
  // it is rewritten to the rate this coupon actually pays, so the headline
  // beside "+R$ 10" can never read "+30%".
  return withTrueRate(raw, c, lang);
}

function couponTerms(c: DepositCoupon, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const principal = Number(c.principalAuditMultiplier ?? 0);
  const bonus = Number(c.bonusAuditMultiplier ?? c.auditMultiplier ?? 0);
  // "deposit 0×" reads as a requirement when it is the absence of one — the
  // deposit itself is free to withdraw and only the bonus carries rollover.
  const key = principal > 0 ? 'depositCouponTerms' : 'depositCouponTermsBonusOnly';
  return t(key)
    .replace('%cap%', fmtMoney(Number(c.maxBonus || 0)))
    .replace('%principal%', fmtInt(principal))
    .replace('%bonus%', fmtInt(bonus));
}

function SandboxCheckoutSimulation({
  order,
  amount,
  onReturn,
}: {
  order: WalletOrder;
  amount: number;
  onReturn: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const txid = depositOrderKey(order);
  return (
    <div className="deposit-sandbox-checkout">
      <div className="deposit-sandbox-checkout-card">
        <span className="deposit-sandbox-badge">SANDBOX</span>
        <h3>{t('sandboxCheckoutTitle')}</h3>
        <p>{t('sandboxCheckoutBody')}</p>
        <div className="deposit-sandbox-checkout-amount">
          {!isInternalPaymentChannel({ channelId: order.channelId, label: order.channelLabel, network: order.network })
            ? <span>{order.network || order.asset || '-'}</span>
            : null}
          <strong>{fmtMoney(amount)}</strong>
        </div>
        <div className="deposit-sandbox-checkout-txid">
          {t('pixDepositQrTxid')}: {txid || '-'}
        </div>
        <p className="deposit-sandbox-checkout-note">{t('sandboxCheckoutNoTransfer')}</p>
        <button className="primary-button sheet-submit" type="button" onClick={onReturn}>
          {t('sandboxCheckoutReturn')}
        </button>
      </div>
    </div>
  );
}

function DepositSheet({
  payer, onPayerChange, provider,
  amount,
  channel,
  order,
  canSubmit,
  pending,
  error,
  minAmount,
  maxAmount,
  coupons,
  couponsLoading,
  couponsError,
  selectedCouponId,
  offerBonus, offerSelected, onSelectOffer,
  appliedBonus,
  onSelectCoupon,
  onAmountChange,
  onRetryCoupons,
  onSubmit,
  onClose,
  onSupport,
  onRecover,
  onPoll,
  onRegenerate,
  embeddedCheckoutOpen,
  onOpenEmbeddedCheckout,
  onExitEmbeddedCheckout,
}: {
  payer: { name: string; cpf: string };
  onPayerChange: (payer: { name: string; cpf: string }) => void;
  amount: number;
  channel?: WalletCashierSettings;
  provider?: WalletPaymentChannel;
  order: WalletOrder | null;
  canSubmit: boolean;
  pending: boolean;
  error: boolean;
  minAmount: number;
  maxAmount: number;
  coupons: DepositCoupon[];
  couponsLoading: boolean;
  couponsError: boolean;
  offerBonus: number;
  offerSelected: boolean;
  onSelectOffer: (selected:boolean)=>void;
  selectedCouponId: number | null;
  appliedBonus: number;
  onSelectCoupon: (id: number | null) => void;
  onAmountChange: (amount: number, immediateQuote?: boolean) => void;
  onRetryCoupons: () => void;
  onSubmit: () => void;
  onClose: () => void;
  onPoll?: () => void;
  onSupport: () => void;
  onRecover?: (publicId: string) => void;
  onRegenerate: () => void;
  embeddedCheckoutOpen: boolean;
  onOpenEmbeddedCheckout: () => void;
  onExitEmbeddedCheckout: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  const lang = useI18nStore((state) => state.language);
  const fiatCurrency = useCountryStore((state) => state.country.fiatCurrency);
  const currencySymbol = fiatCurrencySymbol();
  const internalChannel = isInternalPaymentChannel(provider);
  const depositMethod = provider?.network || provider?.label || '';
  const depositTitle = internalChannel
    ? t('walletDeposit')
    : depositMethod ? t(marketCopyKey('pixDepositTitle', countryCode)).replace('PIX', depositMethod) : t('walletDeposit');
  const [couponPickerOpen, setCouponPickerOpen] = useState(false);
  const [amountInputError, setAmountInputError] = useState(false);
  const [amountDraft, setAmountDraft] = useState(amount > 0 ? String(amount) : '');
  useEffect(() => {setAmountDraft(current => Number(current) === amount ? current : amount > 0 ? String(amount) : '');}, [amount]);
  const [nowTs, setNowTs] = useState(() => Math.floor(Date.now() / 1000));
  const expiresAt = order ? Number(order.expiresAt || 0) || (Number(order.createdAt || 0) + 15 * 60) : 0;
  const expirySeconds = order ? Math.max(0, expiresAt - nowTs) : 0;
  const orderStatus = order ? orderStatusBucket(order.status, 'deposit') : 'pending';
  const checkoutUrl = safeCheckoutUrl(order?.checkoutUrl);
  const sandboxCheckoutFallback = order?.checkoutSandboxFallback === true;
  // A provider create failure is intentionally kept queryable by the backend,
  // but there is nothing the player can open in the meantime. Present that as
  // an actionable error instead of a misleading payment countdown with only a
  // cancel link.
  const checkoutUnavailable = Boolean(
    order && orderStatus === 'pending' && !checkoutUrl && !sandboxCheckoutFallback,
  );
  const lifecycle = order
    ? orderStatus === 'completed' ? 'success' : orderStatus === 'failed' || expirySeconds <= 0 || checkoutUnavailable ? 'failed' : 'waiting'
    : 'pick';
  const iframeCheckout = Boolean(embeddedCheckoutOpen && order && (checkoutUrl || sandboxCheckoutFallback) && order.checkoutMode === 'iframe' && lifecycle === 'waiting');
  useEffect(() => {
    if (!order) return undefined;
    const timer = window.setInterval(() => setNowTs(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [order]);
  useEffect(() => {
    // Keep the provider page uninterrupted. Once the iframe is dismissed, the
    // parent wallet starts the same two-second return-time reconciliation used
    // by external checkout tabs.
    if (!order || iframeCheckout || lifecycle === 'success' || lifecycle === 'failed' || !onPoll) return undefined;
    const timer = window.setInterval(() => onPoll(), 2_000);
    return () => window.clearInterval(timer);
  }, [iframeCheckout, lifecycle, onPoll, order]);
  const orderAmount = Number(order?.requestedAmount ?? order?.receivedAmount ?? amount);
  // Success quotes what landed: the received amount and the bonus the backend
  // settled with it. The pre-submit estimate (appliedBonus) only covers a
  // backend that predates `bonusAmount` — it is never added on top of it.
  const landedAmount = Number(order?.receivedAmount ?? order?.requestedAmount ?? amount);
  const landedBonus = Math.max(0, Number(order?.bonusAmount ?? appliedBonus ?? 0));
  const txid = depositOrderKey(order);
  useEffect(() => {
    if (order) setCouponPickerOpen(false);
  }, [order]);

  if (iframeCheckout) {
    return (
      <div className="sheet-backdrop deposit-v2-backdrop deposit-checkout-frame-backdrop" role="presentation" onMouseDown={onExitEmbeddedCheckout}>
        <section className="wallet-deposit-sheet deposit-checkout-frame-sheet" role="dialog" aria-modal="true" aria-label={depositTitle} onMouseDown={(event) => event.stopPropagation()}>
          <header className="sheet-header">
            <div>
              <h2>{depositTitle}</h2>
              <p>{fmtMoney(orderAmount)}</p>
            </div>
            <button className="icon-button" type="button" onClick={onExitEmbeddedCheckout} aria-label={t('close')}>×</button>
          </header>
          {checkoutUrl ? (
            <iframe className="deposit-checkout-frame" title={depositTitle} src={checkoutUrl} allow="payment *; clipboard-read; clipboard-write" referrerPolicy="strict-origin-when-cross-origin" />
          ) : (
            <SandboxCheckoutSimulation order={order!} amount={orderAmount} onReturn={onExitEmbeddedCheckout} />
          )}
        </section>
      </div>
    );
  }

  function updateAmount(rawValue: string) {
    const trimmed = rawValue.trim();
    setAmountDraft(trimmed);
    if (!trimmed) {
      setAmountInputError(false);
      onAmountChange(0);
      return;
    }
    const normalized = trimmed.replace(',', '.');
    if (!/^\d+(\.\d{0,2})?$/.test(normalized)) {setAmountInputError(true);onAmountChange(0);return;}
    setAmountInputError(false);
    onAmountChange(Number(normalized));
  }

  const selectedCoupon = coupons.find((coupon) => coupon.id === selectedCouponId) || null;
  const selectedBonus = offerSelected ? offerBonus : selectedCoupon ? Number(selectedCoupon.estimatedBonus || 0) : 0;
  const expectedBalance = amount + selectedBonus;
  const amountError = amountInputError
    ? t('depositAmountIntegerError')
    : amount > 0 && amount < minAmount
      ? t('depositAmountMinError').replace('%v%', fmtMoney(minAmount))
      : amount > maxAmount
        ? t('depositAmountMaxError').replace('%v%', fmtMoney(maxAmount))
        : '';
  const availableChips = depositQuickAmounts(minAmount, maxAmount, channel?.quickAmounts);
  const sortedCoupons = [...coupons].sort((a, b) => Number(b.estimatedBonus || 0) - Number(a.estimatedBonus || 0));

  if (!order) {
    const submitLabel = !canSubmit
      ? t('depositAmountRequired')
      : couponsError
        ? t('depositContinueWithoutCoupon').replace('%v%', fmtMoney(amount))
        : t('depositContinueWith').replace('%v%', fmtMoney(amount));

    return (
      <div className="sheet-backdrop deposit-v2-backdrop" role="presentation" onMouseDown={() => { if (!pending) onClose(); }}>
        <section
          className="wallet-deposit-sheet deposit-sheet-v2"
          role="dialog"
          aria-modal="true"
          aria-labelledby="deposit-sheet-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          {/* The coupon list no longer takes over the sheet, so the header has
              one job again. */}
          <header className="deposit-v2-header">
            <div className="sheet-grabber" aria-hidden="true" />
            <div className="deposit-v2-header-row">
              <div>
                <h2 id="deposit-sheet-title">{depositTitle}</h2>
                <p>{t('depositReviewBeforeContinue')}</p>
              </div>
              <button className="deposit-v2-icon-button" type="button" disabled={pending} onClick={onClose} aria-label={t('close')}>×</button>
            </div>
          </header>

            <div className="deposit-v2-body">
              {provider?.requiresPayerIdentity && <section className="deposit-v2-section">
                <label className="deposit-v2-label" htmlFor="deposit-payer-name">{t('payeeFieldNome')}</label>
                <div className="deposit-v2-amount-field"><input id="deposit-payer-name" autoComplete="name" maxLength={128} value={payer.name} onChange={e => onPayerChange({ ...payer, name: e.target.value })} /></div>
                <label className="deposit-v2-label" htmlFor="deposit-payer-cpf">{t('payeeFieldCpf')}</label>
                <div className="deposit-v2-amount-field"><input id="deposit-payer-cpf" inputMode="numeric" autoComplete="off" maxLength={14} value={payer.cpf} onChange={e => onPayerChange({ ...payer, cpf: e.target.value.replace(/\D/g, '').slice(0, 11) })} /></div>
              </section>}
              <section className="deposit-v2-section" aria-labelledby="deposit-amount-label">
                <label id="deposit-amount-label" className="deposit-v2-label" htmlFor="deposit-amount-input">
                  {t('depositAmountQuestion')}
                </label>
                <div className={`deposit-v2-amount-field ${amountError ? 'error' : ''}`}>
                  <span>{currencySymbol}</span>
                  <input
                    id="deposit-amount-input"
                    type="text"
                    inputMode={channel?.amountPrecision === 0 ? "numeric" : "decimal"}
                    enterKeyHint="done"
                    autoComplete="off"
                    aria-label={t('depositAmountAria')}
                    aria-describedby="deposit-amount-help"
                    aria-invalid={!!amountError}
                    value={amountDraft}
                    disabled={channel?.allowCustomAmount === false}
                    placeholder="0"
                    onChange={(event) => updateAmount(event.target.value)}
                  />
                </div>
                <div id="deposit-amount-help" className={`deposit-v2-range ${amountError ? 'error' : ''}`}>
                  <span>{amountError || (channel ? t('depositAmountRange').replace('%min%', fmtMoney(minAmount)).replace('%max%', fmtMoney(maxAmount)) : '')}</span>
                  <span>{channel?.amountPrecision===0?t('depositAmountIntegerOnly'):''}</span>
                </div>
                <div className="deposit-v2-quick-grid" aria-label={t('depositQuickAmounts')}>
                  {availableChips.map((chip) => {
                    const active = amount === chip;
                    return (
                      <button
                        key={chip}
                        className={active ? 'active' : ''}
                        type="button"
                        aria-pressed={active}
                        onClick={() => {
                          setAmountInputError(false);
                          onAmountChange(chip, true);
                        }}
                      >
                        {fmtMoney(chip)}
                        {active ? <span className="deposit-v2-chip-check"><Check size={10} aria-hidden="true" /></span> : null}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* The cashier offer row shares the coupon row's anatomy (dot ·
                  copy · amount) — it is on by default now, so it has to read
                  as a real option, not an unstyled checkbox. */}
              {offerBonus>0&&canSubmit&&<label className={`deposit-v2-coupon-option deposit-v2-offer-option${offerSelected ? ' active' : ''}`}>
                <input type="checkbox" checked={offerSelected} onChange={e=>onSelectOffer(e.target.checked)}/>
                <span className="deposit-v2-radio-dot" aria-hidden="true" />
                <span className="dco-copy">
                  <strong>{t('cashierDepositBonus')}</strong>
                  <small>{t('cashierDepositBonusHint')}</small>
                </span>
                <b className="money">+{fmtMoney(offerBonus)}</b>
              </label>}
              <section className="deposit-v2-section" aria-labelledby="deposit-benefits-label">
                <h3 id="deposit-benefits-label" className="deposit-v2-label">{t('depositBenefitsTitle')}</h3>
                {!canSubmit ? (
                  <div className="deposit-v2-coupon-status pending-amount">
                    <span className="deposit-v2-ticket"><Ticket size={18} aria-hidden="true" /></span>
                    <span>
                      <strong>{t('depositCouponAfterAmount')}</strong>
                      <small>{t('depositCouponAfterAmountSub')}</small>
                    </span>
                  </div>
                ) : couponsLoading ? (
                  <div className="deposit-v2-coupon-skeleton" aria-live="polite">
                    <span />
                    <span />
                    <small>{t('depositCouponsLoading')}</small>
                  </div>
                ) : couponsError ? (
                  <div className="deposit-v2-coupon-status quote-error" role="status">
                    <span className="deposit-v2-error-icon"><AlertTriangle size={18} aria-hidden="true" /></span>
                    <span>
                      <strong>{t('depositCouponsUnavailableTitle')}</strong>
                      <small>{t('depositCouponsUnavailableBody')}</small>
                      <button type="button" onClick={onRetryCoupons}>
                        <RefreshCw size={14} aria-hidden="true" />
                        {t('depositCouponsRetry')}
                      </button>
                    </span>
                  </div>
                ) : coupons.length ? (
                  /* One control, the way a boarding-pass app offers fares: the
                     row states what is applied and what it pays on THIS amount;
                     tapping it opens the whole list in place. "Change" and
                     "Remove" were two links for one decision, and "Remove"
                     looked destructive when it is simply another option — it is
                     the last row of the list now. */
                  <div className={`deposit-v2-coupon-select${couponPickerOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="deposit-v2-coupon-trigger"
                      aria-expanded={couponPickerOpen}
                      onClick={() => setCouponPickerOpen((open) => !open)}
                    >
                      <span className="dcs-head">
                        <strong>{selectedCoupon ? couponName(selectedCoupon, lang) : t('depositCouponNoCoupon')}</strong>
                        <b className={selectedCoupon ? 'money' : ''}>{selectedCoupon ? `+${fmtMoney(selectedBonus)}` : fmtMoney(0)}</b>
                      </span>
                      <span className="dcs-terms">
                        {selectedCoupon ? couponTerms(selectedCoupon, t) : t('depositCouponNoCouponSub')}
                      </span>
                      <span className="dcs-cue">
                        <em>{couponPickerOpen ? t('depositCouponPickerClose') : t('depositCouponChoose').replace('%n%', fmtInt(coupons.length))}</em>
                        <ChevronDown size={16} aria-hidden="true" />
                      </span>
                    </button>
                    {couponPickerOpen ? (
                      <div className="deposit-v2-coupon-options" role="radiogroup" aria-label={t('depositCouponPickerTitle')}>
                        {sortedCoupons.map((coupon, index) => (
                          <label className={`deposit-v2-coupon-option${selectedCouponId === coupon.id ? ' active' : ''}`} key={coupon.id}>
                            <input
                              type="radio"
                              name="deposit-coupon"
                              value={coupon.id}
                              checked={selectedCouponId === coupon.id}
                              onChange={() => {
                                onSelectCoupon(coupon.id);
                                setCouponPickerOpen(false);
                              }}
                            />
                            <span className="deposit-v2-radio-dot" aria-hidden="true" />
                            <span className="dco-copy">
                              <strong>
                                {couponName(coupon, lang)}
                                {index === 0 && sortedCoupons.length > 1 ? <em>{t('depositCouponHighestNow')}</em> : null}
                              </strong>
                              <small>{couponTerms(coupon, t)}</small>
                            </span>
                            <b className="money">+{fmtMoney(Number(coupon.estimatedBonus || 0))}</b>
                          </label>
                        ))}
                        {!coupons.some((c) => /^first_deposit_[1-5]$/.test(c.code)) && <label className={`deposit-v2-coupon-option no-coupon${selectedCouponId === null ? ' active' : ''}`}>
                          <input
                            type="radio"
                            name="deposit-coupon"
                            value="none"
                            checked={selectedCouponId === null}
                            onChange={() => {
                              onSelectCoupon(null);
                              setCouponPickerOpen(false);
                            }}
                          />
                          <span className="deposit-v2-radio-dot" aria-hidden="true" />
                          <span className="dco-copy">
                            <strong>{t('depositCouponNoCoupon')}</strong>
                            <small>{t('depositCouponNoCouponSub')}</small>
                          </span>
                          <b>{fmtMoney(0)}</b>
                        </label>}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="deposit-v2-coupon-status empty-coupons">
                    <span className="deposit-v2-ticket"><Ticket size={18} aria-hidden="true" /></span>
                    <span>
                      <strong>{t('depositCouponNone')}</strong>
                      <small>{t('depositCouponNoCouponSub')}</small>
                    </span>
                  </div>
                )}
              </section>

              {canSubmit ? (
                <section className="deposit-v2-section" aria-labelledby="deposit-summary-label">
                  <h3 id="deposit-summary-label" className="deposit-v2-label">{t('depositSummaryTitle')}</h3>
                  <div className="deposit-v2-summary">
                    <div><span>{t('depositYouPay')}</span><b>{fmtMoney(amount)}</b></div>
                    {selectedCoupon ? <div><span>{t('depositCouponBonus')}</span><b className="money">{fmtMoney(selectedBonus)}</b></div> : null}
                    <div className="total"><strong>{t('depositExpectedBalance')}</strong><b>{fmtMoney(expectedBalance)}</b></div>
                  </div>
                </section>
              ) : null}

              {provider?.network === 'PIX' ? <p className="deposit-v2-pix-note">
                <span aria-hidden="true">◇</span>
                {t('pixDepositTipTiming')}
              </p> : null}
              {error ? <p className="deposit-v2-submit-error" role="alert">{t('pixDepositFailedSub')}</p> : null}
              {/* ARS only: the transfer-receipt reminder closes the scrolling
                  content (PM, 2026-09-15) — a reminder, not a gate — so it
                  never floats over the pay button. Brazil never renders it. */}
              {fiatCurrency === 'ARS' ? <DepositImportantNotice onSupport={onSupport} /> : null}
            </div>

          {/* Choosing a coupon no longer needs its own confirm step: picking a
              row applies it, so the footer only ever offers the deposit. */}
          <footer className="deposit-v2-footer">
            {selectedCoupon && canSubmit ? (
              <div className="deposit-v2-footer-total">
                <span>{t('depositExpectedBalance')}</span>
                <b>{fmtMoney(expectedBalance)}</b>
              </div>
            ) : null}
            <button className="deposit-v2-primary" type="button" disabled={!canSubmit || pending || amountInputError} onClick={onSubmit}>
              {pending ? <><RefreshCw className="deposit-v2-spinner" size={17} aria-hidden="true" /> {t('submitting')}</> : submitLabel}
            </button>
          </footer>
        </section>
      </div>
    );
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="wallet-deposit-sheet" role="dialog" aria-modal="true" aria-label={depositTitle} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        {lifecycle === 'success' ? (
          <>
            {/* Success gets its own single-column layout: the waiting chrome
                (title, amount card, bonus pill) used to stay on screen and the
                success panel repeated every line of it. One headline number —
                what actually landed in the wallet — and one line of context. */}
            {/* The transfer-receipt panel (ARS) belongs to the waiting state and
                to the order detail sheet; on success it only pushed the check
                below a stray "View receipt" button. The close button floats so
                the header adds no empty band above the check. */}
            <header className="sheet-header deposit-success-header">
              <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
            </header>
            <div className="deposit-success" data-deposit-success-react="true">
              <span className="deposit-check check-pop"><Check size={30} strokeWidth={2.5} aria-hidden="true" /></span>
              <h2 className="deposit-success-title">{t('pixDepositSuccess')}</h2>
              <b className="deposit-success-amount">+{fmtMoney(landedAmount + landedBonus)}</b>
              {landedBonus > 0 ? (
                <span className="deposit-success-breakdown">
                  {t('pixDepositSuccessBreakdown').replace('%amount%', fmtMoney(landedAmount)).replace('%bonus%', fmtMoney(landedBonus))}
                </span>
              ) : null}
              <p className="deposit-success-sub">{t('pixDepositSuccessSub')}</p>
              <button className="primary-button sheet-submit" type="button" onClick={onClose}>{t('btnDone')}</button>
              <span className="deposit-success-txid">{t('pixDepositQrTxid')} · {txid ? String(txid) : '-'}</span>
            </div>
          </>
        ) : null}
        {lifecycle !== 'success' ? (
        <header className="sheet-header">
          <div>
            <h2>{checkoutUnavailable ? t('pixDepositUnavailableTitle') : order ? t('pixDepositQrTitle') : depositTitle}</h2>
            <p>{checkoutUnavailable ? t('pixDepositUnavailableSub') : order ? t('pixDepositQrSub') : t('pixDepositChooseAmt')}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>
        ) : null}

        {lifecycle !== 'success' && order?.publicId && proofSupported(order) ? (
          <button className="deposit-cancel-link" type="button" onClick={() => onRecover?.(String(order.publicId))}>{t('unpaidRecoverCta')}</button>
        ) : null}
        {lifecycle !== 'success' ? (
        <div className="deposit-qr-panel">
            <div className="deposit-qr-amount">
              {!internalChannel ? <span>{provider?.network || channel?.asset || '-'}</span> : null}
              <strong>{fmtMoney(orderAmount)}</strong>
            </div>
            {appliedBonus > 0 ? (
              <div className="deposit-qr-bonus">
                {t('depositQrBonus').replace('%total%', fmtMoney(orderAmount + appliedBonus)).replace('%bonus%', fmtMoney(appliedBonus))}
              </div>
            ) : null}
            <div className="deposit-order-meta">
              <span>{t('pixDepositQrTxid')}: {txid ? `${String(txid).slice(0, 16)}…` : '-'}</span>
            </div>
            {lifecycle === 'waiting' ? (
              <>
                <div className="deposit-expiry-line">{t('pixDepositQrExp').replace('%t%', `${String(Math.floor(expirySeconds / 60)).padStart(2, '0')}:${String(expirySeconds % 60).padStart(2, '0')}`)}</div>
                {!iframeCheckout && order.checkoutMode === 'iframe' && (checkoutUrl || sandboxCheckoutFallback) ? (
                  <button className="primary-button sheet-submit" type="button" onClick={onOpenEmbeddedCheckout}>
                    {t('pixDepositOpenCheckout')}
                  </button>
                ) : null}
                <button className="deposit-cancel-link" type="button" onClick={onClose}>{t('pixDepositCancel')}</button>
              </>
            ) : null}
            {lifecycle === 'failed' ? (
              <div className="deposit-state-panel failed" data-deposit-failed-react="true">
                <AlertTriangle size={30} className="text-rose-400" aria-hidden="true" />
                <strong>{t(checkoutUnavailable ? 'pixDepositUnavailableTitle' : 'pixDepositFailedTitle')}</strong>
                <p>{t(checkoutUnavailable ? 'pixDepositUnavailableSub' : 'pixDepositFailedSub')}</p>
                <button className="primary-button sheet-submit" type="button" onClick={onRegenerate}>{t('pixDepositTryAgain')}</button>
              </div>
            ) : null}
        </div>
        ) : null}
      </section>
    </div>
  );
}

function WithdrawSheet({
  provider, step,
  payee,
  payeeForm,
  channel,
  balance,
  eligibility,
  abandonBonus,
  amount,
  fee,
  net,
  request,
  pending,
  error,
  errorCode,
  canSavePayee,
  canContinue,
  canSubmit,
  minAmount,
  maxAmount,
  onStepChange,
  onPayeeFormChange,
  onAmountChange,
  onAbandonBonusChange,
  onSavePayee,
  onSubmit,
  onClose,
  onWithdrawalList,
}: {
  step: WithdrawStep;
  payee: Payee | null;
  payeeForm: Payee;
  channel?: WalletCashierSettings;
  provider?: WalletPaymentChannel;
  balance: number;
  eligibility?: WithdrawalEligibility;
  abandonBonus: boolean;
  amount: number;
  fee: number;
  net: number;
  request: WalletOrder | null;
  pending: boolean;
  error: boolean;
  errorCode?: string;
  canSavePayee: boolean;
  canContinue: boolean;
  canSubmit: boolean;
  minAmount: number;
  maxAmount: number;
  onStepChange: (step: WithdrawStep) => void;
  onPayeeFormChange: (payee: Payee) => void;
  onAmountChange: (amount: number) => void;
  onAbandonBonusChange: (value: boolean) => void;
  onSavePayee: () => void;
  onSubmit: () => void;
  onClose: () => void;
  onWithdrawalList: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  const [amountDraft, setAmountDraft] = useState(amount > 0 ? String(amount) : '');
  useEffect(() => {setAmountDraft(current => Number(current) === amount ? current : amount > 0 ? String(amount) : '');}, [amount]);
  const argentina = useCountryStore((state) => state.country.code) === 'AR';
  const normalizedForm = normalizePayee(payeeForm);
  const status = request ? withdrawUiStatus(request.status) : 'unconfirmed';

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="wallet-deposit-sheet" role="dialog" aria-modal="true" aria-label={t(marketCopyKey('pixWithdrawTitle', countryCode))} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{step === 'payee-form' ? (payee ? t(marketCopyKey('payeeFormEditTitle', countryCode)) : t(marketCopyKey('payeeFormTitle', countryCode))) : step === 'confirm' ? t('pixWithdrawConfirmTitle') : t(marketCopyKey('pixWithdrawTitle', countryCode))}</h2>
            <p>{t('pixWithdrawAvail')} {fmtMoney(balance)}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>×</button>
        </header>

        {step === 'payee-cta' ? (
          <div className="withdraw-stack">
            <div className="payee-cta-card-react">
              <CreditCard size={30} aria-hidden="true" />
              <strong>{t(marketCopyKey('payeeCtaTitle', countryCode))}</strong>
              <p>{t(marketCopyKey('payeeCtaBody', countryCode))}</p>
            </div>
            <button className="primary-button sheet-submit" type="button" onClick={() => onStepChange('payee-form')}>
              {t(marketCopyKey('payeeCtaButton', countryCode))}
            </button>
          </div>
        ) : null}

        {step === 'payee-form' ? (
          <div className="withdraw-stack">
            <label className="field">
              <span>{t('payeeFieldNome')}</span>
              <input
                value={payeeForm.nome}
                maxLength={100}
                autoComplete="name"
                autoCapitalize="words"
                placeholder={t('payeePlaceholderNome')}
                onChange={(event) => onPayeeFormChange({ ...payeeForm, nome: event.target.value })}
              />
              <small>{t('payeeNomeHint')}</small>
            </label>
            <span className="field-label-plain">{t(marketCopyKey('payeeFieldType', countryCode))}</span>
            <div className="payee-type-toggle">
              {argentina ? <>{(['cvu','cbu'] as const).map(type=><button key={type} type="button" className={payeeForm.type===type?'active':''} onClick={()=>onPayeeFormChange({nome:payeeForm.nome,type,taxId:''})}>{type.toUpperCase()}</button>)}</> : <>
              <button type="button" className={payeeForm.type === 'cpf' ? 'active' : ''} onClick={() => onPayeeFormChange({ nome: payeeForm.nome, type: 'cpf', taxId: '' })}>{t('payeeTypeCpf')}</button>
              <button type="button" className={payeeForm.type === 'email' ? 'active' : ''} onClick={() => onPayeeFormChange({ nome: payeeForm.nome, type: 'email', taxId: '' })}>{t('payeeTypeEmail')}</button>
              <button type="button" className={payeeForm.type === 'phone' ? 'active' : ''} onClick={() => onPayeeFormChange({ nome: payeeForm.nome, type: 'phone', taxId: '' })}>{t('payeeTypePhone')}</button>
              <button type="button" disabled={provider?.requiresPayoutDocument} className={payeeForm.type === 'evp' ? 'active' : ''} onClick={() => onPayeeFormChange({ nome: payeeForm.nome, type: 'evp', taxId: '' })}>{t('payeeTypeEvp')}</button>
              </>}
            </div>
            <label className="field">
              <span>{payeeFieldLabel(payeeForm, t)}</span>
              <input
                value={payeeForm.taxId}
                inputMode={payeeInputMode(payeeForm.type)}
                maxLength={payeeMaxLength(payeeForm.type)}
                placeholder={payeePlaceholder(payeeForm.type)}
                onChange={(event) => onPayeeFormChange({ ...payeeForm, taxId: formatPayeeValue(payeeForm.type, event.target.value) })}
              />
              {payeeTaxIdHint(normalizedForm, t) ? <small className="warning">{payeeTaxIdHint(normalizedForm, t)}</small> : null}
            </label>
            {provider?.requiresPayoutDocument && payeeForm.type !== 'cpf' && <label className="field">
              <span>{t('payeeFieldCpf')}</span>
              <input inputMode="numeric" maxLength={14} value={maskCpf(payeeForm.document || '')} onChange={e=>onPayeeFormChange({...payeeForm,document:e.target.value.replace(/\D/g,'').slice(0,11)})}/>
            </label>}
            <button className="primary-button sheet-submit" type="button" disabled={!canSavePayee} onClick={onSavePayee}>
              {t('payeeFormSave')}
            </button>
          </div>
        ) : null}

        {step === 'amount' ? (
          <div className="withdraw-stack">
            {payee ? (
              <div className="payee-saved-card-react">
                <div>
                  <span>{t(marketCopyKey('payeeSavedLabel', countryCode))}</span>
                  <strong>{payee.nome}</strong>
                  <small>{payee.type.toUpperCase()} · {maskPayeeRef(payee)}</small>
                </div>
                <button className="ghost-action" type="button" onClick={() => onStepChange('payee-form')}>{t('payeeEditAction')}</button>
              </div>
            ) : null}
            <label className="field">
              <span>{t('pixWithdrawAmount')}</span>
              <input
                value={amountDraft}
                inputMode="numeric"
                placeholder={t('pixWithdrawAmountPlaceholder')}
                onChange={(event) => {const value=event.target.value.replace(',', '.');if(/^\d*(\.\d{0,2})?$/.test(value)){setAmountDraft(value);onAmountChange(Number(value)||0);}}}
              />
              {channel ? <small>{t('pixWithdrawAmountHint').replace('%min%', fmtMoney(minAmount)).replace('%max%', fmtMoney(maxAmount))}</small> : null}
            </label>
            {!!channel?.quickAmounts?.length&&<div className="deposit-v2-quick-grid">{depositQuickAmounts(minAmount,maxAmount,channel.quickAmounts).map(v=><button type="button" key={v} onClick={()=>onAmountChange(v)}>{fmtMoney(v)}</button>)}</div>}
            <div className="withdraw-preview">
              <span>{t('pixWithdrawFee')} <b>{fmtMoney(fee)}</b></span>
              <strong>{t('pixWithdrawNet')} <b>{fmtMoney(net)}</b></strong>
            </div>
            {eligibility?.hasPendingBonusSources ? <p className="withdraw-warning">{t('pixWithdrawBonusPending')}</p> : null}
            <button className="primary-button sheet-submit" type="button" disabled={!canContinue} onClick={() => onStepChange('confirm')}>
              {t('pixDepositContinue')}
            </button>
          </div>
        ) : null}

        {step === 'confirm' && payee ? (
          <div className="withdraw-stack">
            <div className="withdraw-confirm-amount">
              <span>{t('pixWithdrawNet')}</span>
              <strong>{fmtMoney(net)}</strong>
            </div>
            <div className="withdraw-preview">
              <span>{t(marketCopyKey('pixWithdrawChave', countryCode))} <b>{formatPayeeRef(payee)}</b></span>
              <span>{t('pixWithdrawAmount')} <b>{fmtMoney(amount)}</b></span>
              <span>{t('pixWithdrawFee')} <b>{fmtMoney(fee)}</b></span>
              {eligibility?.requiresBonusAbandonment ? <span>{t('pixWithdrawBonusHeld')} <b>{fmtMoney(Number(eligibility.forfeitedBonusAmount))}</b></span> : null}
              {eligibility?.walletBalanceAfterSuccess != null ? <span>{t('pixWithdrawBalanceAfter')} <b>{fmtMoney(Number(eligibility.walletBalanceAfterSuccess))}</b></span> : null}
            </div>
            <p className="withdraw-warning">{t(marketCopyKey('pixWithdrawConfirmSub', countryCode))}</p>
            {eligibility?.requiresBonusAbandonment ? (
              <label className="withdraw-warning withdraw-abandon-confirm">
                <input type="checkbox" checked={abandonBonus} onChange={(event) => onAbandonBonusChange(event.target.checked)} />
                {t('pixWithdrawAbandonBonus').replace('%v%', fmtMoney(Number(eligibility.forfeitedBonusAmount || 0)))}
              </label>
            ) : null}
            {eligibility?.requiresBonusAbandonment ? <p className="withdraw-warning">{t('pixWithdrawBonusLifecycle')}</p> : null}
            {eligibility?.hasPendingBonusSources ? <p className="withdraw-warning">{t('pixWithdrawBonusPending')}</p> : null}
            {error ? <p className="form-error">{t(errorCode === 'WITHDRAWAL_QUOTE_CHANGED' || errorCode === 'INSUFFICIENT_WITHDRAWABLE_BALANCE' ? 'pixWithdrawQuoteChanged' : 'pixWithdrawFailedSub')}</p> : null}
            <button className="primary-button sheet-submit" type="button" disabled={pending || !canSubmit} onClick={onSubmit}>
              {pending ? t('submitting') : t('pixWithdrawConfirmBtn')}
            </button>
          </div>
        ) : null}

        {step === 'status' ? (
          <div className="withdraw-status-panel" data-withdraw-status-react={status}>
            {status === 'review' || status === 'sent' ? (
              <>
                <span className="deposit-check check-pop"><Check size={34} aria-hidden="true" /></span>
                <strong>{t('pixWithdrawSubmittedTitle')}</strong>
                <span>{t('pixWithdrawSubmittedSub')}</span>
                <small>{t('pixWithdrawSubmittedEta')}</small>
                <button className="primary-button sheet-submit" type="button" onClick={onWithdrawalList}>{t('pixWithdrawSubmittedConfirm')}</button>
              </>
            ) : null}
            {status === 'unconfirmed' ? (
              <>
                <Hourglass size={42} className="text-amber-300" aria-hidden="true" />
                <strong>{t('pixWithdrawUnconfirmedTitle')}</strong>
                <span>{t('pixWithdrawUnconfirmedSub')}</span>
                <button className="primary-button sheet-submit" type="button" onClick={onWithdrawalList}>{t('btnDone')}</button>
              </>
            ) : null}
            {status === 'received' ? (
              <>
                <span className="deposit-check check-pop"><Check size={34} aria-hidden="true" /></span>
                <strong>{t('pixWithdrawReceived')}</strong>
                <b>-{fmtMoney(amount)}</b>
                <div className="withdraw-status-breakdown-react" data-withdraw-status-breakdown-react="true">
                  <span>{t('pixWithdrawNet')} <strong>{fmtMoney(net)}</strong> · {t('pixWithdrawFee')} <strong>{fmtMoney(fee)}</strong></span>
                  {payee ? <span>{t(marketCopyKey('pixWithdrawChave', countryCode))} <strong>{maskedChave(payee)}</strong></span> : null}
                </div>
                <span>{t(marketCopyKey('pixWithdrawReceivedSub', countryCode))}</span>
                <button className="primary-button sheet-submit" type="button" onClick={onClose}>{t('btnDone')}</button>
              </>
            ) : null}
            {status === 'failed' ? (
              <>
                <AlertTriangle size={42} className="text-rose-400" aria-hidden="true" />
                <strong>{t('pixWithdrawFailedTitle')}</strong>
                <span>{t('pixWithdrawFailedSub')}</span>
                <button className="primary-button sheet-submit" type="button" onClick={onClose}>{t('pixWithdrawClose')}</button>
              </>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

type UiStatementRow = {
  id: string;
  type: 'deposit' | 'withdraw' | 'bet' | 'win' | 'reward';
  category: string;
  amount: number;
  balanceAfter: number;
  time: number;
  ref: string;
  betId: string;
};

function normalizeStatement(items: WalletStatementItem[]): UiStatementRow[] {
  return items.map((item) => {
    const category = String(item.category || '').toLowerCase();
    return {
      id: String(item.id),
      type: walletTxType(category),
      category,
      amount: Number(item.amount || 0),
      balanceAfter: Number(item.balanceAfter ?? item.balance_after ?? 0),
      time: Number(item.createdAt ?? item.created_at ?? 0),
      ref: walletTxRef(item, category),
      betId: item.betId != null ? String(item.betId) : '',
    };
  }).filter((row) => row.category && Number.isFinite(row.time));
}

function walletTxType(category: string): UiStatementRow['type'] {
  if (category === 'win') return 'win';
  if (category === 'bet') return 'bet';
  if (category.startsWith('deposit')) return 'deposit';
  if (category.startsWith('withdraw')) return 'withdraw';
  return 'reward';
}

function rowMatchesFilter(row: UiStatementRow, filter: WalletFilter) {
  if (filter === 'all') return true;
  if (filter === 'deposit') return row.type === 'deposit';
  if (filter === 'withdraw') return row.type === 'withdraw';
  if (filter === 'bet') return row.type === 'bet' || row.type === 'win';
  return row.type === 'reward';
}

function walletTxRef(item: WalletStatementItem, category: string) {
  if (item.betId) return `Bet #${item.betId}`;
  // Orders show their public reference (PIX-…), never the row id: those are
  // global sequences, and two of a player's own rows subtracted would give
  // away the site's throughput between them. Where the backend has not sent
  // one yet, say nothing rather than fall back to the id.
  if (item.depositId) return String(item.depositPublicId || '').trim();
  if (item.withdrawId) return String(item.withdrawPublicId || '').trim();
  return item.asset || item.currency || '-';
}

// Human reference line for rows without a bet/deposit/withdraw id — known
// sources get a proper name instead of a raw category slug.
function txRefDisplay(row: UiStatementRow, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (row.category === 'raffle_prize') return t('walletRefRaffle');
  // A bet's id is bets.id — a BIGSERIAL shared by the whole platform, so
  // "#710" never meant the player's 710th bet, and two of a player's own rows
  // subtracted gave away the site's total volume between them. It also
  // answered no question they have: the chip beside the title already says
  // which game and which pick.
  if (row.betId) return '';
  return row.ref;
}

function txCategoryLabel(category: string, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (category === 'win') return t('tx_win');
  if (category === 'bet') return t('tx_bet');
  if (category.startsWith('deposit')) return t('tx_deposit');
  if (category.startsWith('withdraw')) return t('tx_withdraw');
  return t('tx_reward');
}


function withdrawUiStatus(status: string | undefined) {
  const value = String(status || '').toLowerCase();
  if (['paid', 'success', 'completed', 'done', 'adjusted'].includes(value)) return 'received';
  if (['review', 'pending', 'created', 'submitted'].includes(value)) return 'review';
  if (['approved', 'pending_payout', 'processing'].includes(value)) return 'sent';
  if (['rejected', 'cancelled', 'canceled', 'failed', 'expired', 'refused', 'denied'].includes(value)) return 'failed';
  return 'sent';
}

function channelRange(channel: WalletPaymentChannel) {
  return `${fmtMoney(channel.minAmount || 0)} - ${fmtMoney(channel.maxAmount || 0)}`;
}

function depositOrderKey(order: WalletOrder | null) {
  if (!order) return '';
  return String(order.publicId || order.providerRef || order.id || '');
}

function safeCheckoutUrl(value: string | undefined) {
  try {
    const url = new URL(String(value || '').trim());
    return url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
}

function readPayee(): Payee | null {
  try {
    const parsed = JSON.parse(readStorage(localStorage, PAYEE_STORAGE_KEY) || 'null') as Partial<Payee> | null;
    if (!parsed?.nome || !parsed.taxId) return null;
    const type = parsed.type;
    if (type !== 'cpf' && type !== 'email' && type !== 'phone' && type !== 'evp' && type !== 'cvu' && type !== 'cbu') return null;
    return normalizePayee({ nome: String(parsed.nome), type, taxId: String(parsed.taxId), document: String(parsed.document || '') });
  } catch {
    return null;
  }
}

function normalizePayee(payee: Payee): Payee {
  return {
    nome: String(payee.nome || '').slice(0, 100),
    type: payee.type,
    taxId: formatPayeeValue(payee.type, payee.taxId),
    document: String(payee.document || '').replace(/\D/g, '').slice(0,11),
  };
}

function canSavePayee(payee: Payee, requiresDocument = false) {
  if(requiresDocument && (payee.type==='evp' || payee.type!=='cpf' && !/^\d{11}$/.test(payee.document || ''))) return false;
  if (payee.nome.trim().length < 2) return false;
  const value = formatPayeeRef(payee);
  switch (payee.type) {
    case 'cvu': case 'cbu': return /^\d{22}$/.test(value);
    case 'cpf': return /^\d{11}$/.test(value);
    case 'email': return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 160;
    case 'phone': return /^\d{10,15}$/.test(value);
    case 'evp': return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }
}

function payeeTaxIdHint(payee: Payee, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (payee.type !== 'cpf' && payee.type !== 'phone') return '';
  const digits = digitsOnly(payee.taxId);
  const needed = payee.type === 'cpf' ? 11 : 10;
  if (!digits.length || digits.length >= needed) return '';
  return t('payeeTaxIdShortHint').replace('%left%', String(needed - digits.length));
}

function formatPayeeRef(payee: Payee) {
  switch (payee.type) {
    case 'cvu': case 'cbu': return digitsOnly(payee.taxId).slice(0, 22);
    case 'cpf': return digitsOnly(payee.taxId).slice(0, 11);
    case 'email': return String(payee.taxId || '').trim().toLowerCase();
    case 'phone': return digitsOnly(payee.taxId).slice(0, 15);
    case 'evp': return String(payee.taxId || '').trim().toLowerCase();
  }
}

function payeeFieldLabel(payee: Payee, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (payee.type === 'cvu' || payee.type === 'cbu') return payee.type.toUpperCase();
  return payee.type === 'cpf' ? t('payeeFieldCpf') : t('payeeFieldPixKey');
}

function payeeInputMode(type: Payee['type']): 'numeric' | 'email' | 'text' {
  if (type === 'cpf' || type === 'phone' || type === 'cvu' || type === 'cbu') return 'numeric';
  return type === 'email' ? 'email' : 'text';
}

function payeeMaxLength(type: Payee['type']) {
  switch (type) {
    case 'cvu': case 'cbu': return 22;
    case 'cpf': return 14;
    case 'email': return 160;
    case 'phone': return 15;
    case 'evp': return 36;
  }
}

function payeePlaceholder(type: Payee['type']) {
  switch (type) {
    case 'cvu': case 'cbu': return '0000000000000000000000';
    case 'cpf': return '000.000.000-00';
    case 'email': return 'nome@email.com';
    case 'phone': return '5511999999999';
    case 'evp': return '00000000-0000-0000-0000-000000000000';
  }
}

function formatPayeeValue(type: Payee['type'], value: string) {
  switch (type) {
    case 'cvu': case 'cbu': return digitsOnly(value).slice(0, 22);
    case 'cpf': return formatCpf(value);
    case 'email': return String(value || '').trimStart().slice(0, 160);
    case 'phone': return digitsOnly(value).slice(0, 15);
    case 'evp': return String(value || '').toLowerCase().replace(/[^0-9a-f-]/g, '').slice(0, 36);
  }
}

function maskPayeeRef(payee: Payee) {
  const value = formatPayeeRef(payee);
  if (!value) return '';
  if (payee.type === 'cpf') return maskCpf(value);
  if (payee.type === 'email') {
    const [local, domain] = value.split('@');
    return domain ? `${local.slice(0, 2)}***@${domain}` : '***';
  }
  if (payee.type === 'phone') return `***${value.slice(-4)}`;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function formatCpf(value: string) {
  const digits = digitsOnly(value).slice(0, 11);
  if (digits.length > 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  if (digits.length > 6) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  return digits;
}

function maskCpf(value: string) {
  const digits = digitsOnly(value);
  if (digits.length !== 11) return value || '';
  return `***.***.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function feeFor(channel: WalletCashierSettings | undefined, amount: number) {
  if (!channel || amount <= 0) return 0;
  const fee = Math.floor((Number(channel.feeFlat || 0) + amount * Number(channel.feePct || 0) / 100 + 1e-9) * 100) / 100;
  let payout = Math.floor((amount - fee + 1e-9) * 100) / 100;
  const step = Number(channel.payoutStep || 0);
  if (step > 0) payout = Math.floor((payout + 1e-9) / step) * step;
  return Math.max(0, Number((amount - payout).toFixed(2)));
}

function digitsOnly(value: string) {
  return String(value || '').replace(/\D/g, '');
}

function formatTransactionTime(seconds: number) {
  if (!seconds) return '--';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(new Date(seconds * 1000));
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value || '';
    return `${part('year')}/${part('month')}/${part('day')} ${part('hour')}:${part('minute')}:${part('second')}`;
  } catch {
    return '--';
  }
}

function ticketsForUser(tickets: RaffleTicket[], userId: number | string | undefined) {
  const currentId = userId == null ? '' : String(userId);
  return tickets.filter((ticket) => {
    if (!currentId || ticket.user_id == null) return true;
    return String(ticket.user_id) === currentId;
  });
}

function ticketSourceLabel(source: string | undefined, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (source === 'reward_first_deposit') return t('ticketSource_reward_first_deposit');
  if (source === 'reward_invite_deposit') return t('ticketSource_reward_invite_deposit');
  if (source === 'reward_daily_5rounds') return t('ticketSource_reward_daily_5rounds');
  if (source === 'reward_3day_streak') return t('ticketSource_reward_3day_streak');
  if (source === 'reward_first_bet_app') return t('ticketSource_reward_first_bet_app');
  if (source === 'reward_winner' || source === 'raffle_winner') return t('ticketSource_reward_winner');
  if (source === 'admin_manual_grant') return t('ticketSource_bonus');
  // V143 opened three more producers, and `claim_reward` — the path
  // RewardInboxService uses — is the busiest one today. Until these were named,
  // most rows on a real account read "Ticket" and told the player nothing.
  if (source === 'claim_reward') return t('ticketSource_claim_reward');
  if (source === 'reward_phone_verified') return t('ticketSource_reward_phone_verified');
  if (source === 'reward_email_verified') return t('ticketSource_reward_email_verified');
  // Never leak a raw backend source string to users.
  return t('ticketSource_unknown');
}

function eventId(event: RaffleData['event']) {
  return String(event?.event_id || event?.id || '');
}

function maintenanceBlocksWallet(level?: string) {
  return level === 'wallet_only' || level === 'full';
}

export function raffleEventRouteId(event: RaffleRecentEvent) {
  return String(event.event_id || event.id || event.scheduled_at || '');
}

function findRaffleEventByRouteId(events: RaffleRecentEvent[], routeId: string | null) {
  const value = decodeRouteId(routeId);
  if (!value) return null;
  return events.find((event) => {
    const candidates = [
      event.event_id,
      event.id,
      event.scheduled_at,
      raffleEventRouteId(event),
    ];
    return candidates.some((candidate) => decodeRouteId(candidate == null ? '' : String(candidate)) === value);
  }) || null;
}

export function findTargetRaffleResult(data: RaffleData | undefined, routeId: string | null, userId: number | string | undefined): RaffleRecentEvent | null {
  const recentEvents = data?.recent_events || [];
  if (routeId) {
    const recentMatch = findRaffleEventByRouteId(recentEvents, routeId);
    if (recentMatch) return recentMatch;
    const current = currentEventAsRecentResult(data, userId);
    return current && findRaffleEventByRouteId([current], routeId) ? current : null;
  }
  return recentEvents.find(isRaffleResultReady) || currentEventAsRecentResult(data, userId);
}

function currentEventAsRecentResult(data: RaffleData | undefined, userId: number | string | undefined): RaffleRecentEvent | null {
  const event = data?.event;
  if (!event || !isRaffleResultReady(event)) return null;
  const id = eventId(event);
  const myEntries = (data?.entries || []).filter((entry) => sameId(entry.event_id, id) && sameId(entry.user_id, userId));
  const winners = event.winners || [];
  const myEntryIds = new Set(myEntries.map((entry) => String(entry.entry_id || '')).filter(Boolean));
  const mySerials = new Set(myEntries.map((entry) => ticketSerial(entry.serial_no)));
  const myPrize = winners
    .filter((winner) => myEntryIds.has(String(winner.entry_id || '')) || mySerials.has(ticketSerial(winner.serial_no)) || sameId(winner.user_id, userId))
    .reduce((sum, winner) => sum + Number(winner.amount || 0), 0);
  return {
    event_id: event.event_id,
    id: event.id,
    scheduled_at: event.scheduled_at,
    total_entries: event.total_entries,
    d_digits: event.d_digits,
    status: event.status,
    winners,
    my_entries: myEntries,
    my_prize: myPrize,
  };
}

function isRaffleResultReady(event: RaffleRecentEvent | RaffleData['event']) {
  return event?.status === 'settled' || event?.status === 'voided';
}

function sameId(a: unknown, b: unknown) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

// 票号的读法只有一份，在 shared/raffle/ticketSerial.ts。这里曾经有一个私有的
// `serialNumber`（+1），DrawEntriesSheet 里还有一份抄件——票号改成 0 起时两份都
// 没跟上，14 个显示点的号全高了一位（2026-09-25 复核）。别再往这里加解析。

function raffleBracket(event: RaffleData['event']) {
  const total = Number(event?.total_entries || 0);
  return (event?.prize_brackets || []).find((bracket) => {
    const min = Number(bracket.min_n || 0);
    const max = Number(bracket.max_n || Number.MAX_SAFE_INTEGER);
    return total >= min && total <= max;
  }) || (event?.prize_brackets || [])[0];
}

type RaffleTier = NonNullable<NonNullable<NonNullable<RaffleData['event']>['prize_brackets']>[number]['tiers']>[number];

function raffleTierRewards(tier: RaffleTier) {
  if (Array.isArray(tier.rewards) && tier.rewards.length > 0) return tier.rewards;
  return Number(tier.amount || 0) > 0 ? [{ kind: 'cash', amount: Number(tier.amount || 0) }] : [];
}

function raffleTierCashAmount(tier: RaffleTier) {
  const cash = raffleTierRewards(tier).find((reward) => reward.kind === 'cash');
  return Number(cash?.amount || 0);
}

function raffleTierCashTotal(tier: RaffleTier) {
  return Number(tier.count || 0) * raffleTierCashAmount(tier);
}

function raffleCashPool(bracket: ReturnType<typeof raffleBracket>) {
  return (bracket?.tiers || []).reduce((sum, tier) => sum + raffleTierCashTotal(tier), 0);
}

function raffleWinnerCount(bracket: ReturnType<typeof raffleBracket>) {
  return (bracket?.tiers || []).reduce((sum, tier) => sum + Number(tier.count || 0), 0);
}

function raffleTierPrimaryText(tier: RaffleTier, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const count = fmtInt(Number(tier.count || 0));
  const cashAmount = raffleTierCashAmount(tier);
  if (cashAmount > 0) return `${count} × ${fmtMoney(cashAmount)}`;
  return t(Number(tier.count || 0) === 1 ? 'raffleWinnerSingle' : 'raffleWinnerMany').replace('%n%', count);
}

function raffleTierExtras(tier: RaffleTier, t: ReturnType<typeof useI18nStore.getState>['t']) {
  return raffleTierRewards(tier)
    .filter((reward) => reward.kind !== 'cash')
    .map((reward) => {
      if (reward.kind === 'raffle_ticket') return `+${fmtInt(Number(reward.ticket_count || 0))} ${t('raffleTicketShort')}`;
      if (reward.kind === 'coupon') return `+ ${t('raffleDepositCoupon')}`;
      return '';
    })
    .filter(Boolean);
}

function raffleRankMedal(rank: number) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return '🏅';
}

// The verification math in schoolbook words — "÷ … remainder" instead of the
// programmer's "mod", which most players have never seen.
//
// One line now. It used to be two, the second existing only to explain the +1
// that turned a 0-based remainder into a 1-based ticket; tickets start at 000,
// so the remainder IS the winning ticket (2026-09-24 PM). The ticket carries no
// "#" — that prefix means a round number, never a ticket.
function raffleVerifyStep1Text(
  digits: string,
  totalEntries: number,
  serialBase: number,
  t: ReturnType<typeof useI18nStore.getState>['t'],
) {
  if (!digits || totalEntries <= 0) return [] as string[];
  const rest = moduloDecimalString(digits, totalEntries);
  const ticket = rest + (serialBase === 1 ? 1 : 0);
  return [
    t(serialBase === 1 ? 'fairExampleStep1Legacy' : 'fairExampleStep1')
      .split('%d%').join(digits)
      .split('%n%').join(fmtInt(totalEntries))
      .split('%r%').join(fmtInt(rest))
      .split('%f%').join(ticketLabel(ticket)),
  ];
}

// Raffle wall-clock times render in the VIEWER's own timezone, like every other
// clock in the app (product decision 2026-09-10). They used to be pinned to
// `America/Sao_Paulo` so this screen would agree with the Sorteios board — but
// the board was the thing that was wrong, and both moved together.

function formatTicketTime(seconds: number | undefined) {
  if (!seconds) return '--';
  try {
    return new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: false })
      .format(new Date(seconds * 1000));
  } catch {
    return '--';
  }
}

// Entry timestamps are part of the public, auditable ledger — show the exact
// second so two entries a moment apart are visibly distinct (a HH:MM-only view
// makes identical-looking times read as suspicious).
function formatTicketTimeSec(seconds: number | undefined) {
  if (!seconds) return '--';
  try {
    return new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
      .format(new Date(seconds * 1000));
  } catch {
    return '--';
  }
}

// "hoje" / "ontem" / "DD/MM" relative to the (server-synced) now — helps users
// place a result in time. Day boundaries are the VIEWER's, matching the clock
// times beside them and the day headings in My Bets and the archive.
function formatRelativeDay(seconds: number | undefined, nowSeconds: number, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (!seconds) return '';
  try {
    const targetDay = localDayKey(seconds);
    const nowDay = localDayKey(nowSeconds || 0);
    if (targetDay === nowDay) return t('raffleDayToday');
    if (targetDay === localDayKey((nowSeconds || 0) - 86400)) return t('raffleDayYesterday');
    return localDayShort(seconds);
  } catch {
    return '';
  }
}

// Legacy _chaveDisplay: mask the PIX chave in the received screen.
function maskedChave(payee: Payee) {
  return `${payee.type.toUpperCase()} ${maskPayeeRef(payee)}`;
}
